/**
 * API Service for RecubeBE Panel
 * Handles authenticated requests to the Quarkus backend
 */

import { authService } from './auth.service';
import type { PlayerPositionDTO } from '../components/PlayerPositionsCard';
import { useAuthStore } from '../stores/auth.store';
import { useGameStore } from '../stores/game.store';
import { getGameById, DEFAULT_GAME_ID } from '../config/games';
import { contextHeadersFor, GAME_GROUP_REFUSED_EVENT } from '../utils/gameGroups';
import { readUnauthorized, SESSION_ENDED_ERROR } from './sessionPolicy';

export interface UserInfoResponse {
  xuid: string;
  gamertag: string;
  playerUuid: string;
  rank: string;
  displayName: string;
  groups: string[];
  permissions: string[];
  isAdmin?: boolean;
  gradeIds?: string[];
  gradeNames?: string[];
  /** Set complet des permissions <projet>.<page> (tous projets) — le front scope par jeu actif. */
  panelPermissions?: string[];
  manageAccess?: boolean;       // peut ouvrir « Grades & accès »
  canAssignGrades?: boolean;    // peut attribuer des grades
  assignableGradeIds?: string[]; // grades qu'il a le droit d'attribuer
  /** Projets dont le user est admin (toutes les pages du projet, sans gestion des grades). */
  adminProjects?: string[];
  /** Accès d'urgence : base des grades injoignable, droits admin temporaires (cf. EmergencyAccess côté API). */
  emergency?: boolean;
  /**
   * Groupes de jeu permis par projet à groupes (NationsGlory : Nova, Iris...) : clés permises, ou
   * null = tous (aucune affectation, admin global ou du projet). Projet absent = tous ses groupes.
   */
  gameGroups?: Record<string, string[] | null>;
}

export interface AccessCheckResponse {
  hasAccess: boolean;
  gamertag: string | null;
  /** Accès accordé par l'accès d'urgence, pas par un grade. */
  emergency?: boolean;
}

export interface SanctionDTO {
  id: string;
  type: number;
  typeName: string;
  victimName: string | null;
  victimUuid: string | null;
  staffName: string | null;
  staffUuid: string | null;
  /** Pseudos Recube de la victime et du staff ; absents sans pseudo ou sans registre Recube. */
  victimDisplayName?: string | null;
  staffDisplayName?: string | null;
  description: string | null;
  duration: string | null;
  createAt: number | null;
  expiryAt: number | null;
  temporary: boolean;
  revoked: boolean;
  /** Staff qui a levé la sanction et raison de la levée (revoked_name / revoked_reason de la ModerationAPI) ; absents sur les anciennes levées. */
  revokedName?: string | null;
  revokedReason?: string | null;
}

export interface SanctionsResponse {
  items: SanctionDTO[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface TodaySanctionsCount {
  total: number;
  bans: number;
  mutes: number;
  warns: number;
}

// Player types (Split endpoints)
export interface PlayerBasicInfoDTO {
  uuid: string;
  /** Gamertag ; null si la ProfileApi ne connaît pas le joueur (trouvé par son pseudo Recube). */
  name: string | null;
  firstConnected: number | null;
  lastConnected: number | null;
  /** Pseudo Recube du joueur ; absent sans pseudo ou sans registre Recube sur le satellite. */
  displayName?: string | null;
}

export interface PlayerSuggestionDTO {
  uuid: string;
  /** Gamertag (roster du satellite). */
  name: string;
  /** Pseudo Recube du joueur ; absent sans pseudo ou sans registre Recube sur le satellite. */
  displayName?: string | null;
}

/** Joueur tiré au hasard parmi ceux connus du satellite (vue par défaut de la page Joueurs). */
export interface RandomPlayerDTO {
  uuid: string;
  name: string;
  /** Dernière présence vue par le satellite (ms), null si inconnue. */
  lastSeen: number | null;
  /** Première apparition (yyyy-MM-dd), null si inconnue. */
  firstSeenDate: string | null;
  /** En ligne maintenant ; null quand le satellite ne connaît pas la présence. */
  online: boolean | null;
  /** Pseudo Recube du joueur ; absent sans pseudo ou sans registre Recube sur le satellite. */
  displayName?: string | null;
}

export interface RandomPlayersResponse {
  players: RandomPlayerDTO[];
  /** Nombre (estimé) de joueurs connus du satellite. */
  total: number;
}

export interface PlayerRoleDTO {
  role: string;
  roleDisplayName: string;
}

export interface PlayerMoneyDTO {
  money: number;
}

// Legacy full profile type (for reference)
export interface PlayerProfileDTO {
  uuid: string;
  name: string | null;
  firstConnected: number | null;
  lastConnected: number | null;
  role: string | null;
  roleDisplayName: string | null;
  nameHistory: NameHistoryEntry[];
  personalHomes: Record<string, HomeDTO[]>;
  faction: FactionInfoDTO | null;
  jobs: JobsInfoDTO | null;
  sanctions: SanctionDTO[];
}

export interface NameHistoryEntry {
  name: string;
  firstSeen: number | null;
}

// Home list item (sans coordonnees)
export interface HomeListItemDTO {
  name: string;
  world: string;
}

// Home avec coordonnees (perm separee)
export interface HomeCoordsDTO {
  name: string;
  server: string;
  world: string;
  x: number;
  y: number;
  z: number;
}

// Legacy HomeDTO (pour compatibilite)
export interface HomeDTO {
  name: string;
  x: number;
  y: number;
  z: number;
  world: string;
}

export interface FactionInfoDTO {
  id: string;
  name: string;
  description: string | null;
  leaderUuid: string | null;
  playerRoles: string[];
  bankBalance: number | null;
  experience: number | null;
  elo: number | null;
  claimsCount: number;
  membersCount: number;
  homes: FactionHomeDTO[];
}

export interface FactionHomeDTO {
  server: string;
  world: string;
  x: number;
  y: number;
  z: number;
}

export interface FactionMemberDTO {
  uuid: string;
  name: string | null;
  roles: string[];
  /** Pseudo Recube du joueur ; absent sans pseudo ou sans registre Recube sur le satellite. */
  displayName?: string | null;
}

export interface FactionClaimDTO {
  server: string;
  type: string;
  count: number;
}

export interface JobsInfoDTO {
  jobs: JobDTO[];
  dailyQuests: DailyQuestDTO[];
}

export interface JobDTO {
  id: string;
  name: string;
  xp: number;
}

export interface DailyQuestDTO {
  name: string;
  description: string;
  jobId: string;
  iconId: string;
  targetValue: number;
  currentProgress: number;
  xpReward: number;
  completed: boolean;
}

// Market types
export interface MarketItemDTO {
  id: string;
  identifier: string;
  name: string;
  count: number;
  price: number;
  pbPrice: number;
  createdDate: number | null;
  expirationDate: number | null;
}

export interface MarketHistoryItemDTO {
  id: string;
  identifier: string;
  name: string;
  count: number;
  price: number;
  pbPrice: number;
  createdDate: number | null;
  /** « sale » : le joueur a vendu ; « purchase » : le joueur a acheté. */
  type: 'sale' | 'purchase';
  /** L'autre joueur de l'échange : l'acheteur d'une vente, le vendeur d'un achat. */
  counterpartUuid: string | null;
  counterpartName: string | null;
  /** Pseudo Recube de la contrepartie ; absent sans pseudo ou sans registre Recube. */
  counterpartDisplayName?: string | null;
}

export interface MarketItemsResponse {
  items: MarketItemDTO[];
  totalItems: number;
}

export interface MarketHistoryResponse {
  items: MarketHistoryItemDTO[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface MarketHistoryCountResponse {
  count: number;
}

// Store Points types
export interface StorePointsDTO {
  classicStorePoints: number;
  seasonalStorePoints: number;
  totalStorePoints: number;
}

export interface StorePointTransactionDTO {
  id: string;
  playerUuid: string;
  amount: number;
  type: number;
  timestamp: number;
  reason: string | null;
}

export interface StorePointTransactionsResponse {
  items: StorePointTransactionDTO[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Vault (money) transaction types
export interface VaultTransactionDTO {
  id: string;
  senderUuid: string;
  senderName: string | null;
  amount: number;
  createdDate: number;
  /** Pseudo Recube de l'émetteur ; absent sans pseudo ou sans registre Recube. */
  senderDisplayName?: string | null;
  /** Ce que le mod a dit du mouvement (le détail quand il a nommé sa catégorie) ; absent s'il n'a rien dit. */
  reason?: string | null;
  /** Le mod qui a demandé le mouvement et sa méthode, reconnus par VaultMod ; absents des anciennes transactions. */
  source?: string | null;
  sourceCall?: string | null;
  /** Catégorie (clé de la liste de l'API Vault) et sens ; absents d'une transaction écrite avant ces champs. */
  category?: string | null;
  flow?: string | null;
}

export interface VaultTransactionsResponse {
  items: VaultTransactionDTO[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Console/Server types
export interface ServerDTO {
  identifier: string;
  uuid: string;
  name: string;
  description: string;
  status: string;
  suspended: boolean;
  playerCount: number;
  /** Groupe de jeu du serveur (RECUBE_GROUP), satellite de groupe seulement (NationsGlory). */
  group?: string;
}

export interface ServersResponse {
  servers: ServerDTO[];
  total: number;
}

/**
 * Ticket d'ouverture de la console, à usage unique et valable 30 s.
 *
 * Remplace l'ancien couple {token, socket} de Pterodactyl : le navigateur ne reçoit plus de
 * token d'infrastructure et ne se connecte plus à Pterodactyl. Il ouvre une socket sur le panel,
 * qui relaie vers le satellite du projet, qui relaie vers Pterodactyl.
 */
export interface ConsoleTicket {
  ticket: string;
  /** Chemin prêt à l'emploi, ex `/api/console/stream?ticket=...`. */
  path: string;
  expiresInSeconds: number;
  /** Groupe de jeu du ticket (NationsGlory seulement) : la console ne s'ouvre que sur lui. */
  group?: string;
}

export interface RestartReasonsResponse {
  reasons: string[];
}

export interface RestartResponse {
  success: boolean;
  message: string;
}

export interface BroadcastResponse {
  success: boolean;
  message: string;
}

// Scheduled Messages types
export interface ScheduledMessageDTO {
  id: string;
  serverIdentifier: string;
  message: string;
  intervalMinutes: number;
  enabled: boolean;
  createdAt: number | null;
  createdBy: string | null;
  lastSentAt: number | null;
}

export interface ScheduledMessagesResponse {
  messages: ScheduledMessageDTO[];
}

// Banwords types
export interface BanwordDTO {
  word: string;
  regex: boolean;
}

export interface BanwordsResponse {
  banwords: BanwordDTO[];
  total: number;
}

// Whitelist types
export interface WhitelistEntryDTO {
  id: string;
  /** Gamertag (la whitelist est tenue par gamertag). */
  name: string;
  create_at: number;
  /** Pseudo Recube du joueur ; absent sans pseudo ou sans registre Recube sur le satellite. */
  displayName?: string | null;
  /** Réponse d'un ajout seulement (satellite 1.0.24 ou plus), cf WhitelistProxyNoticeDTO. */
  proxy?: WhitelistProxyNoticeDTO | null;
}

export interface WhitelistResponse {
  entries: WhitelistEntryDTO[];
  total: number;
}

/**
 * Proxys prévenus d'un changement de noms pendant que la liste du réseau est allumée (ils relisent
 * leurs noms), sinon pourquoi. Absent quand la liste est éteinte : l'allumage relit les noms.
 */
export interface WhitelistProxyNoticeDTO {
  notified: boolean;
  message?: string | null;
}

/** Retrait ou vidage de la liste du réseau. */
export interface WhitelistChangeDTO {
  success: boolean;
  message: string;
  proxy?: WhitelistProxyNoticeDTO | null;
}

/** Liste blanche du réseau (proxy), allumée ou éteinte (satellite 1.0.24 ou plus). */
export interface WhitelistStatusDTO {
  /** Ce groupe a une liste du réseau (faux sur un groupe commun comme le lobby NationsGlory). */
  network: boolean;
  enabled: boolean;
  /** Les proxys peuvent être prévenus d'un changement (RabbitMQ déclaré au satellite). */
  proxySync: boolean;
  proxySyncError?: string | null;
}

/** Liste blanche d'un serveur de jeu (/whitelist de ModerationMod). */
export interface ServerWhitelistDTO {
  identifier: string;
  name: string;
  /** État Pterodactyl : running, offline, starting... */
  status: string;
  group?: string | null;
  /** Fichier du mod lu dans le volume ; faux : état et noms inconnus. */
  readable: boolean;
  enabled: boolean;
  names: string[];
}

export interface ServerWhitelistActionDTO {
  server: ServerWhitelistDTO;
  /** Le changement a été relu dans le fichier du serveur. */
  confirmed: boolean;
  message: string;
}

// Logs types
export interface LogEntryDTO {
  id: string;
  timestamp: string;
  method: string;
  uri: string;
  queryParams: string | null;
  statusCode: number;
  durationMs: number;
  username: string;
  userAgent: string | null;
  // Note: clientIp is intentionally excluded for security
  /** Groupe de jeu visé (NationsGlory : nova, iris), absent sans groupe (Paladium, panel). */
  gameGroup?: string;
}

export interface LogsResponse {
  logs: LogEntryDTO[];
  total: number;
  page: number;
  totalPages: number;
  size: number;
}

export interface LogsBucketDTO {
  key: string;
  count: number;
}

export interface LogsTimeSeriesDTO {
  timestamp: string;
  count: number;
}

export interface LogsStatsResponse {
  byMethod: LogsBucketDTO[];
  byUser: LogsBucketDTO[];
  byStatus: LogsBucketDTO[];
  byUri: LogsBucketDTO[];
  avgDurationMs: number;
  requestsOverTime: LogsTimeSeriesDTO[];
}

export interface LogsParams {
  page?: number;
  size?: number;
  username?: string;
  method?: string;
  uri?: string;
  statusCode?: number;
  from?: string;
  to?: string;
}

// Stats / Economy types
export interface JobXpBreakdownEntry {
  jobId: string;
  jobName: string;
  xpGained: number;
  percentage: number;
}

export interface JobXpDailyStatsDTO {
  date: string;
  jobs: JobXpBreakdownEntry[];
  totalXpGained: number;
}

export interface JobXpBreakdownResponse {
  days: JobXpDailyStatsDTO[];
  count: number;
}

// ==================== ADMIN SHOP, FACTIONS ET PLATEFORMES (satellite 1.0.26) ====================

export interface AdminShopItemDTO {
  item: string;
  quantity: number;
  money: number;
  transactions: number;
  /** Prix moyen d'une unité sur la période. */
  unitPrice: number;
}

export interface AdminShopPlayerDTO {
  rank: number;
  uuid: string;
  name?: string;
  displayName?: string;
  money: number;
  transactions: number;
}

export interface AdminShopResponse {
  now: number;
  available: boolean;
  reason?: string;
  days: number;
  /** Première transaction de l'admin shop connue. */
  since?: number;
  totals?: { spent: number; earned: number; buys: number; sells: number; buyers: number; sellers: number };
  daily: { date: string; spent: number; earned: number; buys: number; sells: number }[];
  sold: AdminShopItemDTO[];
  bought: AdminShopItemDTO[];
  /** Achats dont l'objet n'est pas connu (AdminShopMod d'avant la 0.0.23). */
  buysWithoutItem: number;
  buysWithoutItemMoney: number;
  topBuyers: AdminShopPlayerDTO[];
  topSellers: AdminShopPlayerDTO[];
  namesPartial: boolean;
}

export interface FactionRowDTO {
  id: string;
  name?: string;
  members: number;
  techLevel: number;
  branches: Record<string, number>;
  bank: number;
  experience: number;
  league: string;
  seasonalElo: number;
  createdAt?: number;
  open: boolean;
}

/** Un lot de box : objet (et quantité), argent, niveaux d'expérience, points boutique, ou plusieurs à la fois. */
export interface BoxLotDTO {
  lot: string;
  rarity: string;
  rarityIndex: number;
  item?: string | null;
  amount: number;
  customName?: string | null;
  money: number;
  experience: number;
  storePoints: number;
  seasonal: boolean;
}

export interface BoxLotRowDTO extends BoxLotDTO {
  box: string;
  draws: number;
  players: number;
  observedRate: number;
  expectedRate?: number | null;
}

export interface BoxRowDTO {
  box: string;
  kind: string;
  name?: string | null;
  names: Record<string, string>;
  openings: number;
  draws: number;
  players: number;
  legendary: number;
  legendaryRate: number;
  legendaryExpectedRate?: number | null;
  legendaryExpected?: number | null;
  drawsPerOpening?: number | null;
}

export interface BoxRarityDTO {
  rarity: string;
  index: number;
  draws: number;
  observedRate: number;
  expectedRate?: number | null;
  expectedDraws?: number | null;
}

export interface BoxDayDTO {
  date: string;
  openings: number;
  draws: number;
  legendary: number;
  byBox: Record<string, number>;
}

export interface BoxOpenerDTO {
  rank: number;
  uuid: string;
  name?: string;
  displayName?: string;
  openings: number;
  draws: number;
  legendary: number;
  byBox: Record<string, number>;
}

export interface BoxLegendaryDTO {
  at: number;
  uuid: string;
  name?: string;
  displayName?: string;
  box: string;
  lot: string;
  item?: string;
  amount: number;
  customName?: string;
  money: number;
  experience: number;
  storePoints: number;
  seasonal: boolean;
}

/** Type d'achat de la boutique : produit d'une section, cosmétique de la sélection du jour, pack de cosmétiques. */
export type ShopKind = 'product' | 'cosmetic' | 'pack';

/**
 * Totaux de la boutique sur la période : achats gardés (sans les remboursés), remboursements, acheteurs distincts,
 * produits vendus, points dépensés et rendus (saisonniers et classiques).
 */
export interface ShopTotalsDTO {
  purchases: number;
  refunds: number;
  buyers: number;
  products: number;
  spentSeasonal: number;
  spentClassic: number;
  refundSeasonal: number;
  refundClassic: number;
}

/**
 * Un produit sur la période. `key` = type et identifiant (« product:key_amethyst_x5 ») ; `keys` : clés vendues, pour
 * un produit de clés ou de box ; `buyerIds` : numéros des acheteurs propres à la réponse, pour compter sans doublon les
 * acheteurs de plusieurs produits (une catégorie).
 */
export interface ShopProductDTO {
  key: string;
  kind: ShopKind;
  product: string;
  purchases: number;
  refunds: number;
  buyers: number;
  spentSeasonal: number;
  spentClassic: number;
  refundSeasonal: number;
  refundClassic: number;
  keys?: number | null;
  lastAt?: number | null;
  lastPrice?: number | null;
  buyerIds: number[];
}

/** Un jour : achats gardés, acheteurs, points, et pour chaque produit ses achats et ses points nets. */
export interface ShopDayDTO {
  date: string;
  purchases: number;
  buyers: number;
  spentSeasonal: number;
  spentClassic: number;
  refund: number;
  products: Record<string, { purchases: number; points: number }>;
}

export interface ShopBuyerDTO {
  rank: number;
  uuid: string;
  name?: string;
  displayName?: string;
  purchases: number;
  /** Points nets dépensés (rendus déduits). */
  points: number;
  lastAt: number;
}

export interface ShopPurchaseDTO {
  at: number;
  uuid: string;
  name?: string;
  displayName?: string;
  kind: ShopKind;
  product: string;
  seasonal: number;
  classic: number;
  /** Les points de cet achat ont été rendus (livraison ratée). */
  refunded: boolean;
}

/**
 * Totaux des mouvements des points boutique : créés, dépensés (rendus déduits : consumed), la part passée à d'autres
 * joueurs (ventes du market, échanges), celle sortie du jeu, les retraits hors dépense (clear) et les soldes remis.
 */
export interface StorePointFlowTotalsDTO {
  created: number;
  spent: number;
  refunded: number;
  consumed: number;
  toPlayers: number;
  destroyed: number;
  cleared: number;
  sets: number;
  createdPlayers: number;
  spendingPlayers: number;
}

export interface StorePointSourceDTO {
  id: string;
  amount: number;
  count: number;
  players: number;
}

export interface StorePointDestinationDTO {
  id: string;
  spent: number;
  refunded: number;
  net: number;
  toPlayers: number;
  destroyed: number;
  count: number;
  players: number;
}

export interface StorePointFlowDayDTO {
  date: string;
  created: number;
  consumed: number;
  bySource: Record<string, number>;
  byDestination: Record<string, number>;
}

/** Retrait hors dépense (subtracted, other_out) ou solde remis (set : amount est le nouveau solde). */
export interface StorePointClearDTO {
  at: number;
  uuid: string;
  name?: string;
  displayName?: string;
  category: string;
  amount: number;
  seasonal: boolean;
  reason: string;
}

/** Mouvements des points boutique (satellite 1.0.34), lus dans l'historique de StorePointApi. */
export interface StorePointFlowsResponse {
  now: number;
  available: boolean;
  reason?: string;
  days: number;
  type: 'seasonal' | 'classic' | 'all';
  since?: number;
  totals?: StorePointFlowTotalsDTO;
  sources: StorePointSourceDTO[];
  destinations: StorePointDestinationDTO[];
  daily: StorePointFlowDayDTO[];
  clears: StorePointClearDTO[];
  /** Motifs que le satellite ne classe pas encore, regroupés par leur début ; direction in (crédit) ou out (retrait). */
  unknown: { reason: string; direction: 'in' | 'out'; amount: number; count: number }[];
  namesPartial: boolean;
}

/** Boutique en jeu (achats de RecubeBoutiqueMod lus par le satellite 1.0.33 dans l'historique des points boutique). */
export interface ShopStatsResponse {
  now: number;
  available: boolean;
  reason?: string;
  days: number;
  since?: number;
  totals?: ShopTotalsDTO;
  products: ShopProductDTO[];
  daily: ShopDayDTO[];
  topBuyers: ShopBuyerDTO[];
  recent: ShopPurchaseDTO[];
  namesPartial: boolean;
}

/** Un indicateur des personas : sa source, sa fenêtre, et s'il a pu être lu (sinon la raison). */
export interface PersonaIndicatorDTO {
  id: string;
  label: string;
  source: string;
  /** period (sur la période), cumul (depuis la mise en service) ou current (aujourd'hui). */
  window: string;
  /** count, money, xp ou flag (oui ou non). */
  unit: string;
  detail: string;
  available: boolean;
  /** Joueurs qui ont fait l'action au moins une fois (population des centiles). */
  players: number;
  reason?: string;
}

/** Un persona : ses indicateurs et leurs poids, et sa place dans la répartition globale. */
export interface PersonaDTO {
  id: string;
  label: string;
  weight: number;
  indicators: Record<string, number>;
  /** Joueurs dont c'est le persona principal. */
  dominantPlayers: number;
  /** Part moyenne du persona sur les joueurs mesurés (les parts moyennes font 100). */
  averageShare: number;
  available: boolean;
}

/** Un joueur de la liste : sa part entière par persona (les parts font 100), son persona principal, son activité mesurée. */
export interface PersonaPlayerRowDTO {
  uuid: string;
  name?: string;
  displayName?: string;
  shares: Record<string, number>;
  dominant: string;
  /** 0 à 100 : moyenne des scores des personas, dit sur combien de jeu reposent les parts. */
  activity: number;
}

/** Personas des joueurs (satellite 1.0.31) : liste, recherche, tri par persona et répartition globale. */
export interface PersonasResponse {
  now: number;
  available: boolean;
  reason?: string;
  days: number;
  computedAt: number;
  population: number;
  personas: PersonaDTO[];
  indicators: PersonaIndicatorDTO[];
  query?: string;
  persona?: string;
  page: number;
  size: number;
  total: number;
  totalPages: number;
  rows: PersonaPlayerRowDTO[];
  namesPartial: boolean;
  warnings?: string[];
}

/** Un indicateur dans la fiche d'un joueur : sa valeur, son centile (0 à 100) et son poids dans le persona. */
export interface PersonaContributionDTO {
  indicator: string;
  label: string;
  weight: number;
  value: number;
  centile: number;
  available: boolean;
}

export interface PersonaDetailDTO {
  id: string;
  label: string;
  weight: number;
  /** Score du persona, 0 à 100 (moyenne pondérée des centiles). */
  score: number;
  share: number;
  exactShare: number;
  contributions: PersonaContributionDTO[];
}

/** Fiche des personas d'un joueur. */
export interface PlayerPersonasResponse {
  now: number;
  days: number;
  computedAt: number;
  uuid: string;
  name?: string;
  displayName?: string;
  /** Faux : aucun indicateur du joueur n'est positif, pas de parts. */
  measured: boolean;
  dominant?: string;
  activity: number;
  personas: PersonaDetailDTO[];
  indicators: PersonaIndicatorDTO[];
}

/** Box et lootbox (journal de PremiumMod 0.0.80, lu par le satellite) sur une période, une box ou toutes. */
export interface BoxStatsResponse {
  now: number;
  available: boolean;
  reason?: string;
  days: number;
  box?: string;
  since?: number;
  totals?: { openings: number; draws: number; players: number; legendary: number; legendaryExpected?: number; gone: number };
  daily: BoxDayDTO[];
  boxes: BoxRowDTO[];
  rarities: BoxRarityDTO[];
  lots: BoxLotRowDTO[];
  ranks: { rank: string; openings: number; draws: number; drawsPerOpening: number }[];
  topOpeners: BoxOpenerDTO[];
  legendaries: BoxLegendaryDTO[];
  namesPartial: boolean;
}

export interface FactionsResponse {
  now: number;
  available: boolean;
  reason?: string;
  season?: { number: number; startedAt: number; plannedEndAt?: number };
  factions: number;
  members: number;
  averageMembers: number;
  medianMembers: number;
  maxMembers: number;
  openFactions: number;
  /** Joueurs connus de FactionApi, en faction ou non, et ceux vus ces 7 derniers jours (satellite 1.0.28). */
  players?: {
    total: number;
    inFaction: number;
    withoutFaction: number;
    active: number;
    activeInFaction: number;
    activeWithoutFaction: number;
  };
  sizes: { label: string; factions: number }[];
  tech?: {
    maxLevel: number;
    averageLevel: number;
    levels: { level: number; factions: number }[];
    branches: { key: string; averageTier: number; maxTier: number; tiers: { level: number; factions: number }[] }[];
    researching: number;
  };
  bank?: { total: number; average: number; median: number; max: number };
  leagues: { league: string; factions: number }[];
  created: { date: string; factions: number }[];
  table: FactionRowDTO[];
  history: { date: string; factions: number; members: number; bank: number; avgTech: number; season?: number }[];
  snapshotEnabled: boolean;
}

// ==================== Pays (NationsGlory, satellite 1.0.29) ====================

export interface CountryRowDTO {
  id: string;
  name?: string;
  /** Nom du pays au roster quand il diffère du nom choisi par ses membres. */
  country?: string;
  members: number;
  power: number;
  /** Revendications, maisons comprises. */
  claims: number;
  houses: number;
  rdLevel: number;
  branches: Record<string, number>;
  bank: number;
  /** PIB, absent tant que NGCountryAPI ne l'a pas calculé. */
  gdp?: number;
  league: string;
  seasonalElo: number;
  warsOngoing: number;
  warsWon: number;
  warsEnded: number;
  unesco: number;
  createdAt?: number;
  open: boolean;
}

export interface CountryAmountsDTO { total: number; average: number; median: number; max: number }

export interface CountriesResponse {
  now: number;
  available: boolean;
  reason?: string;
  season?: { number: number; startedAt: number };
  countries: number;
  members: number;
  averageMembers: number;
  medianMembers: number;
  maxMembers: number;
  openCountries: number;
  roster?: { total: number; listed: number; taken: number; free: number; unlisted: number };
  players?: { total: number; inCountry: number; withoutCountry: number; active: number; activeInCountry: number; activeWithoutCountry: number };
  sizes: { label: string; countries: number }[];
  power?: CountryAmountsDTO;
  bank?: CountryAmountsDTO;
  gdp?: CountryAmountsDTO & { computed: number };
  territory?: { claims: number; houses: number; average: number; median: number; max: number; worlds: { key: string; count: number }[] };
  rd?: {
    maxLevel: number;
    averageLevel: number;
    levels: { level: number; countries: number }[];
    branches: { key: string; averageTier: number; maxTier: number; tiers: { level: number; countries: number }[] }[];
    researching: number;
  };
  /** Vide tant qu'aucun pays n'est classé. */
  leagues: { key: string; count: number }[];
  wars?: {
    total: number; requested: number; ongoing: number; ended: number;
    declared: { date: string; count: number }[];
    endReasons: { key: string; count: number }[];
    ongoingList: { id: string; attacker?: string; defender?: string; attackerPoints: number; defenderPoints: number; pointsToWin: number; since: number }[];
  };
  unesco?: { accepted: number; pending: number; refused: number; wonders: number };
  created: { date: string; count: number }[];
  table: CountryRowDTO[];
  history: { date: string; countries: number; members: number; power: number; bank: number; gdp?: number; claims: number; avgRd: number; warsOngoing: number; season?: number }[];
  snapshotEnabled: boolean;
}

export interface PlatformSliceDTO {
  key: string;
  players: number;
  minutes: number;
}

export interface PlatformsResponse {
  now: number;
  onlineTotal: number;
  online: PlatformSliceDTO[];
  onlineInput: PlatformSliceDTO[];
  days: number;
  /** Premier jour qui porte la plateforme (début du suivi). */
  since?: string;
  period: PlatformSliceDTO[];
  periodInput: PlatformSliceDTO[];
  /** Joueurs de la période vus seulement avant le suivi. */
  untracked: number;
}

// ==================== ONBOARDING (satellite 1.0.35) ====================
// Routes GET /api/stats/onboarding/* du satellite ÉCRIVAIN (Paladium : son satellite ; NationsGlory : le
// groupe qui annonce la capacité « onboarding », forcé par la page). Pourcentages arrondis à 0,1, délais en
// secondes, montants en euros ; null = pas de donnée (base vide, source absente).

/** Une clé et son libellé (critère, étape du tuto, grade, groupe). */
export interface OnboardingKeyLabel {
  key: string;
  label: string;
}

/** Ce que sert le satellite : puces, colonnes, groupes, sources branchées et dates du suivi. */
export interface OnboardingMetaResponse {
  /** Faux : ce satellite n'écrit pas l'onboarding (les autres routes répondent 404). */
  writer: boolean;
  project: string;
  projectLabel: string;
  /** Puces de critères disponibles pour ce projet, dans l'ordre d'affichage. */
  criteria: OnboardingKeyLabel[];
  /** Colonnes « critère d'intégration » du tableau Intégration (Chat, Faction rejointe, Pays rejoint). */
  integration: OnboardingKeyLabel[];
  /** Étapes du tuto, vide sans tuto (section masquée). */
  tutorialSteps: OnboardingKeyLabel[];
  /** Délais médians du tuto servis (sinon colonnes masquées). */
  tutorialDelays: boolean;
  /** Grades boutique du projet, du plus haut au plus bas. */
  ranks: OnboardingKeyLabel[];
  /** Groupes de jeu (vide pour un projet sans groupes : une seule ligne). */
  groups: OnboardingKeyLabel[];
  /** Source boutique en euros branchée (sinon section Conversion masquée). */
  store: boolean;
  chat: boolean;
  /** Empreinte des IP configurée (sinon pas de double compte). */
  doubleAccount: boolean;
  /** Plus ancienne donnée (reprise de l'historique), yyyy-MM-dd. */
  trackedSince: string | null;
  /** Début du suivi détaillé (lobby, serveur de jeu, factions, pays, grades, tuto), yyyy-MM-dd. */
  detailedSince: string | null;
  reprise: 'none' | 'running' | 'done';
}

/** Valeur d'une carte et sa variation relative (%) face à la même période un an avant ; null = pas de données. */
export interface OnboardingMetric {
  value: number | null;
  change: number | null;
}

export interface OnboardingOverviewResponse {
  registrations: OnboardingMetric;
  loginInGameRate: OnboardingMetric;
  played1hRate: OnboardingMetric;
  /** Secondes. */
  avgPlaytime: OnboardingMetric;
  doubleAccountRate: OnboardingMetric;
  previous: { startDate: string; endDate: string; registrations: number };
  trackedSince: string | null;
}

/** Seau d'inscriptions : `date` = début du seau (jour, lundi, 1er du mois). */
export interface OnboardingDateCount {
  date: string;
  count: number;
}

export interface OnboardingRegistrationsResponse {
  data: OnboardingDateCount[];
  /** Même nombre de seaux, un an avant ; vide sans compare=true. */
  previous: OnboardingDateCount[];
}

/** Branche du tuto du parcours d'accès. */
export interface OnboardingTutoCounts {
  arrived: number;
  done: number;
  abandoned: number;
  lostDuring: number;
  lostAfter: number;
  noTuto: number;
  noTutoGame: number;
  noTutoLost: number;
}

export interface OnboardingFunnelResponse {
  registrations: number;
  gameLogin: number;
  lostBeforeGame: number;
  /** null sans étapes de tuto configurées. */
  tuto: OnboardingTutoCounts | null;
  played5: number;
  under5: number;
  played1h: number;
  under1h: number;
  focus: string[];
}

export interface OnboardingTutorialStepRow {
  key: string;
  label: string;
  count: number;
  pctOfArrived: number | null;
  pctOfPrev: number | null;
  medianFromPrev: number | null;
  medianFromArrived: number | null;
}

export interface OnboardingTutorialFlowStep {
  key: string;
  label: string;
  reached: number;
  /** Joueurs dont c'est la dernière étape atteinte. */
  stopped: number;
}

export interface OnboardingTutorialResponse {
  data: OnboardingTutorialStepRow[];
  flow: { entered: number; stoppedBeforeFirst: number; steps: OnboardingTutorialFlowStep[] } | null;
  delays: boolean;
}

/** Part d'un critère et délai médian (s) depuis l'inscription, sur ceux qui l'ont fait. */
export interface OnboardingCriterionCell {
  pct: number | null;
  medianDelay: number | null;
}

/** Ligne du tableau Intégration : un groupe, « (sans groupe) » (`none`), Ensemble (`all`) ou le projet. */
export interface OnboardingAccessRow {
  group: string;
  label: string;
  players: number;
  played5minPct: number | null;
  played1hPct: number | null;
  criteria: Record<string, OnboardingCriterionCell>;
  /** Part des abandons en moins de 2 jours, null si la base est vide. */
  abandon2d: number | null;
  abandonBase: number;
}

export interface OnboardingAccessResponse {
  data: OnboardingAccessRow[];
  total: OnboardingAccessRow;
}

export interface OnboardingConversionRow {
  group: string;
  label: string;
  players: number;
  store: OnboardingCriterionCell | null;
  rank: OnboardingCriterionCell | null;
  /** Part exclusive de chaque grade (le plus haut du joueur). */
  ranks: Record<string, number | null>;
  ca: number | null;
  avgPerPlayer: number | null;
  medianPerPlayer: number | null;
  avgPerBuyer: number | null;
  medianPerBuyer: number | null;
}

export interface OnboardingConversionResponse {
  data: OnboardingConversionRow[];
  total: OnboardingConversionRow;
}

/** Seau du temps de jeu : `max` = borne haute incluse en secondes, null pour le dernier. */
export interface OnboardingBucket {
  bucket: string;
  max: number | null;
  players: number;
}

export interface OnboardingDistributionResponse {
  data: OnboardingBucket[];
  previous: OnboardingBucket[];
}

/** Filtres communs des routes de l'onboarding. */
export interface OnboardingQuery {
  startDate: string;
  endDate: string;
  /** Clé d'un groupe (groupe principal du joueur) ; absent = tous. */
  group?: string | null;
  /** Critères séparés par des virgules, « ! » = négation. */
  flags?: string;
  /** Nœuds du parcours séparés par des virgules (parcours et tuto seulement). */
  focus?: string;
}

// ==================== STATISTIQUES DES MÉTIERS (satellite 1.0.25) ====================

/** État du relevé horaire du satellite (source des tendances). */
export interface JobTrackingDTO {
  enabled: boolean;
  /** Premier relevé (début du suivi), absent avant. */
  since?: number;
  lastRun?: number;
  retentionDays: number;
  runs: number;
}

export interface JobSummaryDTO {
  jobId: string;
  jobName: string;
  players: number;
  totalXp: number;
  /** Part de l'XP de tous les métiers, en pour cent. */
  share: number;
  averageXp: number;
  medianXp: number;
  topXp: number;
}

export interface JobQuestCountDTO {
  key: string;
  label?: string;
  assigned: number;
  completed: number;
  xpCompleted: number;
}

export interface JobsOverviewResponse {
  now: number;
  /** Faux sur un satellite sans base des métiers (lobby) : `reason` dit pourquoi. */
  available: boolean;
  reason?: string;
  players: number;
  totalXp: number;
  jobs: JobSummaryDTO[];
  boosts?: { activePlayers: number; byName: { name: string; players: number }[] };
  quests?: { day: string; players: number; byType: JobQuestCountDTO[]; byJob: JobQuestCountDTO[] };
  tracking: JobTrackingDTO;
  methods?: { available: boolean; since?: number };
}

export interface JobLevelCountDTO {
  level: number;
  fromXp: number;
  toXp?: number;
  players: number;
}

export interface JobLevelsDTO {
  jobId: string;
  jobName: string;
  players: number;
  maxLevel?: number;
  levels?: JobLevelCountDTO[];
  buckets?: { fromXp: number; toXp: number; players: number }[];
}

export interface JobLevelsResponse {
  now: number;
  curves: boolean;
  jobs: JobLevelsDTO[];
}

export interface JobTopRowDTO {
  rank: number;
  uuid: string;
  name?: string;
  displayName?: string;
  online: boolean;
  serverName?: string;
  xp: number;
  share: number;
  jobs: Record<string, number>;
  bestJob?: string;
}

export interface JobTopResponse {
  now: number;
  job?: string;
  totalPlayers: number;
  totalXp: number;
  page: number;
  size: number;
  totalPages: number;
  query?: string;
  players: JobTopRowDTO[];
  jobs: string[];
  namesPartial: boolean;
}

export interface JobTrendDayDTO {
  date: string;
  gained: number;
  players: number;
  newcomers: number;
  lost: number;
  jobs: { jobId: string; jobName: string; gained: number; players: number }[];
}

export interface JobTrendsResponse {
  now: number;
  days: number;
  tracking: JobTrackingDTO;
  jobs: string[];
  series: JobTrendDayDTO[];
  hours: { hour: number; gained: number; byJob: Record<string, number> }[];
  recordedDays: number;
}

export interface JobGainerRowDTO {
  rank: number;
  uuid: string;
  name?: string;
  displayName?: string;
  online: boolean;
  serverName?: string;
  gained: number;
  activeDays: number;
  byJob: Record<string, number>;
}

export interface JobGainersResponse {
  now: number;
  days: number;
  job?: string;
  tracking: JobTrackingDTO;
  players: JobGainerRowDTO[];
  namesPartial: boolean;
}

export interface JobMethodsResponse {
  now: number;
  days: number;
  job?: string;
  /** Le journal des méthodes des mods existe (JobsMod 0.0.48, RecubeJobsMod 0.0.12 et plus). */
  available: boolean;
  since?: number;
  totalXp: number;
  totalActions: number;
  kinds: { kind: string; xp: number; count: number; share: number }[];
  targets: { kind: string; target: string; jobId: string; jobName: string; xp: number; count: number }[];
  servers: { server: string; xp: number; count: number }[];
  daily: { date: string; kind: string; xp: number }[];
}

export interface EconomyCurrentResponse {
  totalMoney: number;
  playerCount: number;
  averageBalance: number;
  lastSnapshotDate: string | null;
  lastSnapshotTimestamp: number | null;
}

export interface EconomySnapshotDTO {
  date: string;
  timestamp: number;
  totalMoney: number;
  playerCount: number;
  averageBalance: number;
}

export interface EconomyHistoryResponse {
  snapshots: EconomySnapshotDTO[];
  total: number;
}

export interface EconomyBreakdownResponse {
  totalMoney: number;
  playerCount: number;
  averageBalance: number;
  medianBalance: number;
  activeMoney: number;
  activePlayers: number;
  dormantMoney: number;
  dormantPlayers: number;
}

// Stats / Store Points types
export interface StorePointsCurrentResponse {
  totalPoints: number;
  playerCount: number;
  averagePoints: number;
  lastSnapshotDate: string | null;
  lastSnapshotTimestamp: number | null;
}

export interface StorePointsSnapshotDTO {
  date: string;
  timestamp: number;
  totalPoints: number;
  playerCount: number;
  averagePoints: number;
}

export interface StorePointsHistoryResponse {
  snapshots: StorePointsSnapshotDTO[];
  total: number;
}

export interface ApiError {
  message: string;
  /** Code stable d'un refus de la passerelle (ex. GAME_GROUP_REQUIRED), absent sinon. */
  code?: string;
  /** Groupe de jeu concerné par le refus ou par l'erreur du relais du satellite. */
  group?: string;
  /** Erreur produite par le relais du satellite d'entrée (satellite du groupe injoignable...). */
  relay?: boolean;
}

/**
 * En-têtes de contexte de tout appel au backend : `X-Project` (jeu actif) et, seulement pour un jeu
 * à groupes (NationsGlory), `X-Game-Group`. Paladium ne reçoit donc que `X-Project`, comme avant.
 *
 * @param gameGroup groupe imposé (une requête par groupe) ; omis = groupe actif du jeu
 */
export function contextHeaders(gameGroup?: string | null): Record<string, string> {
  const { activeGameId, gameGroupsKnown, activeGameGroupByGame } = useGameStore.getState();
  return contextHeadersFor(activeGameId, gameGroupsKnown, activeGameGroupByGame, gameGroup);
}

/** Options d'un appel : `gameGroup` impose le groupe de jeu de CETTE requête. */
export interface RequestOptions {
  gameGroup?: string | null;
}

// ==================== WATCHDOG (crashs/freezes serveur) ====================
export interface WatchdogReportSummary {
  id: string;
  project: string;
  receivedAt: number;
  event: string | null;
  trigger: string | null;
  timestampUtc: string | null;
  serverName: string | null;
  worstLevelState: string | null;
  worstLevelName: string | null;
  minTps: number | null;
  memoryPercent: number | null;
  threadCount: number | null;
}
export interface WatchdogLevel {
  Name: string;
  State: string;
  LastTick: number;
  SecondsFrozen: number;
  Tps: number;
  Mspt: number;
  AsyncTps: number;
  AsyncMspt: number;
  TickDiagnostics?: string | null;
}
export interface WatchdogThread {
  ManagedThreadId: number;
  OsThreadId: number;
  Name?: string | null;
  IsGc: boolean;
  Frames: string[];
  LockNotes?: string[];
  IsLevelThread?: boolean;
}
export interface WatchdogFreezeReport {
  TimestampUtc: string;
  ServerName: string;
  Trigger: string;
  Levels: WatchdogLevel[];
  Threads: WatchdogThread[];
  ThreadPool?: { ThreadCount: number; PendingWorkItems: number; CompletedWorkItems: number } | null;
  Gc?: { TotalMemoryBytes: number; Gen0Collections: number; Gen1Collections: number; Gen2Collections: number } | null;
  LockAnalysis: string[];
  Memory?: { UsedBytes: number; LimitBytes: number; Percent: number } | null;
}
export interface WatchdogReportDoc extends WatchdogReportSummary {
  report: WatchdogFreezeReport;
}

export interface SchematicDTO {
  name: string;
  size: number;
}

export interface SchematicsResponse {
  files: SchematicDTO[];
}

class ApiService {
  /**
   * Base URL du backend PRINCIPAL partagé (celui du jeu par défaut = Paladium, proxifié via /api).
   * Il héberge des ressources communes à tous les projets : l'auth (/api/auth), la gestion
   * panel (/api/panel) et la configuration/packs (/api/packs). Ces appels ne doivent JAMAIS
   * passer par l'API du jeu actif (ex. /api-ng, /api-recube — placeholders NON proxifiés qui
   * renvoient le fallback SPA index.html en 200 HTML et cassent tout parsing JSON).
   */
  private get mainBaseUrl(): string {
    return getGameById(DEFAULT_GAME_ID).apiBaseUrl;
  }

  /**
   * Base des appels API : backend UNIQUE (multi-projet). Le projet actif est transmis via l'en-tête
   * X-Project (cf request()), et le backend choisit l'infra .26/.27 par projet. On ne route donc plus
   * par jeu via des bases distinctes (fini /api-ng) : tout passe par le backend principal.
   */
  private baseFor(_path: string): string {
    return this.mainBaseUrl;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: RequestOptions,
    /** Second envoi après un 401 reçu pendant qu'un autre onglet renouvelait le jeton (une seule fois). */
    resent = false,
  ): Promise<{ data?: T; error?: string; status?: number; code?: string }> {
    const token = authService.getToken();

    if (!token) {
      return { error: 'Not authenticated' };
    }

    try {
      const response = await fetch(`${this.baseFor(path)}${path}`, {
        method,
        cache: 'no-store', // jamais de réponse d'API mise en cache (sinon un vieux fallback SPA HTML pourrait être servi)
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          // X-Project (projet actif → le backend choisit l'infra .26/.27) et, pour un jeu à groupes,
          // X-Game-Group (la passerelle choisit le satellite du groupe).
          ...contextHeaders(options?.gameGroup),
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      // Handle empty response body (204) + réponses non-JSON (ex. page 502 HTML pendant un redémarrage API).
      const text = await response.text();
      let data: unknown = null;
      let parseFailed = false;
      if (text) {
        try { data = JSON.parse(text); } catch { parseFailed = true; }
      }

      if (!response.ok) {
        // `status` n'est lu que par les pages qui réagissent à un code précis (ex. 409 → proposer
        // de forcer) ; les autres appelants ne voient que `error`, comme avant.
        const status = response.status;
        const refusedCode = (data as ApiError | null)?.code;
        // 403 d'un groupe de jeu hors de l'affectation (GAME_GROUP_FORBIDDEN) : traité plus bas comme
        // les autres refus de groupe (motif, code, relecture des groupes), pas comme un refus de page.
        const groupRefusal = typeof refusedCode === 'string' && refusedCode.startsWith('GAME_GROUP_');
        if (response.status === 403 && !groupRefusal) return { error: 'Permission denied', status };
        if (response.status === 401) {
          const current = authService.getToken();
          // Jeton renouvelé par un autre onglet pendant l'appel : l'API a refusé l'ancien avant de
          // rien faire, la même demande repart une fois avec le jeton à jour.
          if (current && current !== token && !resent) return this.request<T>(method, path, body, options, true);
          const auth = useAuthStore.getState();
          // Lecture du 401 (cf sessionPolicy) :
          // - session finie (jeton échu, refusé, durée maximale) : on le DIT, sans rien jeter de la
          //   page (fenêtre « Ta session a expiré, reconnecte-toi : tes modifications sont gardées »,
          //   reconnexion dans un autre onglet) ;
          // - plus de jeton du tout (déconnexion dans un autre onglet) : déconnexion ici aussi ;
          // - jeton encore bon refusé sans motif de session : souci côté API (assertion satellite,
          //   jwt.secret...). On n'éjecte pas (un seul endpoint fautif ne doit pas déconnecter tout
          //   le monde), on le SIGNALE par le bandeau, sinon les pages restent vides sans motif.
          const verdict = readUnauthorized(refusedCode, !!current, !!current && !authService.isTokenValid(current));
          if (auth.isAuthenticated) {
            if (verdict.kind === 'logout') auth.logout();
            else if (verdict.kind === 'end') auth.markSessionEnded(verdict.end);
            else auth.markSessionRejected();
          }
          return {
            error: verdict.kind === 'rejected' ? 'Refusé par le serveur (401) alors que ta session est valide.' : SESSION_ENDED_ERROR,
            status,
            ...(typeof refusedCode === 'string' ? { code: refusedCode } : {}),
          };
        }
        // Corps illisible OU vide sur une 5xx = réponse d'un proxy / API en redémarrage, pas un
        // message applicatif : « HTTP 502 » tout seul n'aide personne.
        if (parseFailed || (!text && response.status >= 500)) {
          return { error: `Serveur momentanément indisponible (HTTP ${response.status}), réessaie dans un instant.`, status };
        }
        const code = refusedCode;
        // Groupe de jeu refusé par la passerelle (retiré du CICD, sans satellite, hors de l'affectation
        // du membre du staff...) : le layout relit les groupes pour que le sélecteur et le repli
        // suivent. Jamais émis pour Paladium.
        if (typeof code === 'string' && code.startsWith('GAME_GROUP_')) {
          window.dispatchEvent(new CustomEvent(GAME_GROUP_REFUSED_EVENT, { detail: { code } }));
        }
        return {
          error: (data as ApiError)?.message || `HTTP ${response.status}`,
          status,
          ...(typeof code === 'string' ? { code } : {}),
        };
      }

      if (parseFailed) return { error: 'Serveur momentanément indisponible, réessaie dans un instant.' };
      return { data: data as T };
    } catch (err) {
      console.error('API request failed:', err);
      return { error: err instanceof Error ? err.message : 'Request failed' };
    }
  }

  /**
   * Get current user info (from /api/auth/me)
   */
  async getCurrentUser(): Promise<{ data?: UserInfoResponse; error?: string }> {
    return this.request<UserInfoResponse>('GET', '/api/auth/me');
  }

  /**
   * Jeton neuf pour la session en cours (cf session.ts) : même session, nouvelle échéance. Hors de
   * request() : un refus ici ne passe pas par le traitement des 401 des pages, session.ts tranche
   * (le jeton a pu changer pendant l'appel).
   */
  async refreshSessionToken(token: string): Promise<{
    data?: { token: string; expiresAt: number; sessionEndsAt: number };
    error?: string;
    status?: number;
    code?: string;
  }> {
    try {
      const response = await fetch(`${this.mainBaseUrl}/api/auth/refresh`, {
        method: 'POST',
        cache: 'no-store',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Project': useGameStore.getState().activeGameId,
        },
      });
      const text = await response.text();
      let data: unknown = null;
      try { data = text ? JSON.parse(text) : null; } catch { /* corps non JSON (proxy, redémarrage) */ }
      if (!response.ok) {
        const code = (data as ApiError | null)?.code;
        return {
          error: (data as ApiError | null)?.message ?? `HTTP ${response.status}`,
          status: response.status,
          ...(typeof code === 'string' ? { code } : {}),
        };
      }
      const d = data as { token?: unknown; expiresAt?: unknown; sessionEndsAt?: unknown } | null;
      if (!d || typeof d.token !== 'string') return { error: 'Réponse illisible', status: response.status };
      return { data: { token: d.token, expiresAt: Number(d.expiresAt), sessionEndsAt: Number(d.sessionEndsAt) } };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Request failed' };
    }
  }

  /**
   * « Déconnexion » côté serveur : la session ne se renouvellera plus. Part même si la page se ferme
   * juste après (keepalive) ; un échec n'empêche pas la déconnexion du navigateur.
   */
  async logoutSession(token: string): Promise<void> {
    try {
      await fetch(`${this.mainBaseUrl}/api/auth/logout`, {
        method: 'POST',
        cache: 'no-store',
        keepalive: true,
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Project': useGameStore.getState().activeGameId,
        },
      });
    } catch {
      // Réseau coupé : le jeton quitte quand même le navigateur.
    }
  }

  // ==================== WATCHDOG (crashs/freezes serveur) ====================
  /** Liste des rapports de crash/freeze du projet actif (résumés). */
  async getWatchdogReports(): Promise<{ data?: { reports: WatchdogReportSummary[] }; error?: string }> {
    return this.request<{ reports: WatchdogReportSummary[] }>('GET', '/api/watchdog/reports');
  }

  /** Rapport complet (enveloppe + FreezeReport). */
  async getWatchdogReport(id: string): Promise<{ data?: WatchdogReportDoc; error?: string }> {
    return this.request<WatchdogReportDoc>('GET', `/api/watchdog/reports/${encodeURIComponent(id)}`);
  }

  // ==================== PROFILEUR (profileur de tick des serveurs de jeu) ====================
  // Module « profiler » du satellite : mesures envoyées par le mod compagnon des serveurs de jeu, lues
  // par la page Déploiement > Profileur. Les appels typés vivent dans deploy/pages/profiler/profiler.api.ts ;
  // ici, seulement le transport. Lecture seule : l'ingestion ne passe jamais par le panel.

  /**
   * GET sur /api/profiler/servers{sub}. `gameGroup` impose le groupe de jeu de CETTE requête (la page
   * interroge chaque groupe du projet) ; omis, c'est le groupe actif, ou aucun (Paladium).
   */
  async profilerRequest<T>(sub: string, gameGroup?: string | null): Promise<{ data?: T; error?: string; status?: number }> {
    return this.request<T>('GET', `/api/profiler/servers${sub}`, undefined, gameGroup ? { gameGroup } : undefined);
  }

  /**
   * Serveurs de jeu connus de la Console (Pterodactyl) pour un groupe : la page Profileur y repère ceux
   * qui n'envoient aucune mesure. Même route que la page Console, avec le groupe imposé.
   */
  async profilerKnownServers(gameGroup?: string | null): Promise<{ data?: ServersResponse; error?: string; status?: number }> {
    return this.request<ServersResponse>('GET', '/api/console/servers', undefined, gameGroup ? { gameGroup } : undefined);
  }

  /**
   * Get sanctions with pagination
   */
  async getSanctions(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    type?: number;
  }): Promise<{ data?: SanctionsResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.set('page', params.page.toString());
    if (params.pageSize !== undefined) queryParams.set('pageSize', params.pageSize.toString());
    if (params.search) queryParams.set('search', params.search);
    if (params.type !== undefined) queryParams.set('type', params.type.toString());

    const queryString = queryParams.toString();
    const path = `/api/sanctions${queryString ? `?${queryString}` : ''}`;

    return this.request<SanctionsResponse>('GET', path);
  }

  async getTodaySanctionsCount(): Promise<{ data?: TodaySanctionsCount; error?: string }> {
    return this.request<TodaySanctionsCount>('GET', '/api/sanctions/today/count');
  }

  /**
   * Crée une sanction (écriture directe en base de modération).
   * type : 0=ban, 1=mute, 2=warn. `temporary` + `durationMs` pour tempban/mute.
   */
  async createSanction(payload: {
    player: string;
    type: number;
    temporary: boolean;
    durationMs?: number;
    reason: string;
  }): Promise<{ data?: SanctionDTO; error?: string }> {
    return this.request<SanctionDTO>('POST', '/api/sanctions', {
      ...payload,
      durationMs: payload.durationMs ?? 0,
    });
  }

  /** Historique des sanctions posées par un membre du staff (par UUID, fallback par nom). */
  async getSanctionsByStaff(
    uuid: string,
    name?: string,
    page: number = 0,
    pageSize: number = 25
  ): Promise<{ data?: SanctionsResponse; error?: string }> {
    const params = new URLSearchParams();
    if (name) params.set('name', name);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    return this.request<SanctionsResponse>('GET', `/api/sanctions/staff/${encodeURIComponent(uuid)}?${params.toString()}`);
  }

  // ==================== PLAYER SPLIT ENDPOINTS ====================

  /**
   * Search player by name - returns basic info
   */
  async searchPlayer(name: string): Promise<{ data?: PlayerBasicInfoDTO; error?: string }> {
    return this.request<PlayerBasicInfoDTO>('GET', `/api/players/search?name=${encodeURIComponent(name)}`);
  }

  /**
   * Autocomplete: suggere des pseudos (prefixe) parmi les joueurs uniques connus
   */
  async suggestPlayers(query: string, limit = 8): Promise<{ data?: PlayerSuggestionDTO[]; error?: string }> {
    const res = await this.request<{ suggestions: PlayerSuggestionDTO[] }>(
      'GET',
      `/api/players/suggest?q=${encodeURIComponent(query)}&limit=${limit}`
    );
    if (res.error) return { error: res.error };
    return { data: res.data?.suggestions ?? [] };
  }

  /**
   * Joueurs connus du satellite du groupe actif, tirés au hasard (vue par défaut de la page Joueurs).
   * `status` 404 : satellite d'avant cette route, la page montre alors un état neutre.
   */
  async getRandomPlayers(limit = 24): Promise<{ data?: RandomPlayersResponse; error?: string; status?: number }> {
    return this.request<RandomPlayersResponse>('GET', `/api/players/random?limit=${limit}`);
  }

  // --- Basic Info ---
  async getMyBasicInfo(): Promise<{ data?: PlayerBasicInfoDTO; error?: string }> {
    return this.request<PlayerBasicInfoDTO>('GET', '/api/players/me/basic');
  }

  async getBasicInfoByUuid(uuid: string): Promise<{ data?: PlayerBasicInfoDTO; error?: string }> {
    return this.request<PlayerBasicInfoDTO>('GET', `/api/players/${uuid}/basic`);
  }

  // --- Name History ---
  async getMyNameHistory(): Promise<{ data?: NameHistoryEntry[]; error?: string }> {
    return this.request<NameHistoryEntry[]>('GET', '/api/players/me/name-history');
  }

  async getNameHistoryByUuid(uuid: string): Promise<{ data?: NameHistoryEntry[]; error?: string }> {
    return this.request<NameHistoryEntry[]>('GET', `/api/players/${uuid}/name-history`);
  }

  // --- Homes (liste sans coordonnees) ---
  async getMyHomes(): Promise<{ data?: Record<string, HomeListItemDTO[]>; error?: string }> {
    return this.request<Record<string, HomeListItemDTO[]>>('GET', '/api/players/me/homes');
  }

  async getHomesByUuid(uuid: string): Promise<{ data?: Record<string, HomeListItemDTO[]>; error?: string }> {
    return this.request<Record<string, HomeListItemDTO[]>>('GET', `/api/players/${uuid}/homes`);
  }

  async getPlayerPositionsByUuid(uuid: string): Promise<{ data?: PlayerPositionDTO[]; error?: string }> {
    return this.request<PlayerPositionDTO[]>('GET', `/api/players/${uuid}/positions`);
  }

  // --- Home Coordinates (perm separee) ---
  async getMyHomeCoords(server: string, homeName: string): Promise<{ data?: HomeCoordsDTO; error?: string }> {
    return this.request<HomeCoordsDTO>('GET', `/api/players/me/homes/${encodeURIComponent(server)}/${encodeURIComponent(homeName)}/coords`);
  }

  async getHomeCoordsbyUuid(uuid: string, server: string, homeName: string): Promise<{ data?: HomeCoordsDTO; error?: string }> {
    return this.request<HomeCoordsDTO>('GET', `/api/players/${uuid}/homes/${encodeURIComponent(server)}/${encodeURIComponent(homeName)}/coords`);
  }

  // --- Faction ---
  async getMyFaction(): Promise<{ data?: FactionInfoDTO | null; error?: string }> {
    return this.request<FactionInfoDTO | null>('GET', '/api/players/me/faction');
  }

  async getFactionByUuid(uuid: string): Promise<{ data?: FactionInfoDTO | null; error?: string }> {
    return this.request<FactionInfoDTO | null>('GET', `/api/players/${uuid}/faction`);
  }

  async getFactionMembers(factionId: string): Promise<{ data?: FactionMemberDTO[]; error?: string }> {
    return this.request<FactionMemberDTO[]>('GET', `/api/players/faction/${encodeURIComponent(factionId)}/members`);
  }

  async getFactionClaims(factionId: string): Promise<{ data?: FactionClaimDTO[]; error?: string }> {
    return this.request<FactionClaimDTO[]>('GET', `/api/players/faction/${encodeURIComponent(factionId)}/claims`);
  }

  // --- Jobs ---
  async getMyJobs(): Promise<{ data?: JobsInfoDTO | null; error?: string }> {
    return this.request<JobsInfoDTO | null>('GET', '/api/players/me/jobs');
  }

  async getJobsByUuid(uuid: string): Promise<{ data?: JobsInfoDTO | null; error?: string }> {
    return this.request<JobsInfoDTO | null>('GET', `/api/players/${uuid}/jobs`);
  }

  // --- Sanctions ---
  async getMySanctions(): Promise<{ data?: SanctionDTO[]; error?: string }> {
    return this.request<SanctionDTO[]>('GET', '/api/players/me/sanctions');
  }

  async getSanctionsByUuid(uuid: string): Promise<{ data?: SanctionDTO[]; error?: string }> {
    return this.request<SanctionDTO[]>('GET', `/api/players/${uuid}/sanctions`);
  }

  // --- Role ---
  async getMyRole(): Promise<{ data?: PlayerRoleDTO; error?: string }> {
    return this.request<PlayerRoleDTO>('GET', '/api/players/me/role');
  }

  async getRoleByUuid(uuid: string): Promise<{ data?: PlayerRoleDTO; error?: string }> {
    return this.request<PlayerRoleDTO>('GET', `/api/players/${uuid}/role`);
  }

  // --- Money ---
  async getMyMoney(): Promise<{ data?: PlayerMoneyDTO; error?: string }> {
    return this.request<PlayerMoneyDTO>('GET', '/api/players/me/money');
  }

  async getMoneyByUuid(uuid: string): Promise<{ data?: PlayerMoneyDTO; error?: string }> {
    return this.request<PlayerMoneyDTO>('GET', `/api/players/${uuid}/money`);
  }

  // --- Market ---
  async getMyMarketItems(): Promise<{ data?: MarketItemsResponse; error?: string }> {
    return this.request<MarketItemsResponse>('GET', '/api/players/me/market/items');
  }

  async getMarketItemsByUuid(uuid: string): Promise<{ data?: MarketItemsResponse; error?: string }> {
    return this.request<MarketItemsResponse>('GET', `/api/players/${uuid}/market/items`);
  }

  async getMyMarketHistory(page: number = 0, pageSize: number = 10): Promise<{ data?: MarketHistoryResponse; error?: string }> {
    return this.request<MarketHistoryResponse>('GET', `/api/players/me/market/history?page=${page}&pageSize=${pageSize}`);
  }

  async getMarketHistoryByUuid(uuid: string, page: number = 0, pageSize: number = 10): Promise<{ data?: MarketHistoryResponse; error?: string }> {
    return this.request<MarketHistoryResponse>('GET', `/api/players/${uuid}/market/history?page=${page}&pageSize=${pageSize}`);
  }

  async getMyMarketHistoryCount(): Promise<{ data?: MarketHistoryCountResponse; error?: string }> {
    return this.request<MarketHistoryCountResponse>('GET', '/api/players/me/market/history/count');
  }

  async getMarketHistoryCountByUuid(uuid: string): Promise<{ data?: MarketHistoryCountResponse; error?: string }> {
    return this.request<MarketHistoryCountResponse>('GET', `/api/players/${uuid}/market/history/count`);
  }

  // --- Store Points ---
  async getMyStorePoints(): Promise<{ data?: StorePointsDTO; error?: string }> {
    return this.request<StorePointsDTO>('GET', '/api/players/me/storepoints');
  }

  async getStorePointsByUuid(uuid: string): Promise<{ data?: StorePointsDTO; error?: string }> {
    return this.request<StorePointsDTO>('GET', `/api/players/${uuid}/storepoints`);
  }

  async getMyStorePointTransactions(page: number = 0, pageSize: number = 10): Promise<{ data?: StorePointTransactionsResponse; error?: string }> {
    return this.request<StorePointTransactionsResponse>('GET', `/api/players/me/storepoints/transactions?page=${page}&pageSize=${pageSize}`);
  }

  async getStorePointTransactionsByUuid(uuid: string, page: number = 0, pageSize: number = 10): Promise<{ data?: StorePointTransactionsResponse; error?: string }> {
    return this.request<StorePointTransactionsResponse>('GET', `/api/players/${uuid}/storepoints/transactions?page=${page}&pageSize=${pageSize}`);
  }

  // ==================== VAULT TRANSACTIONS ====================

  async getMyMoneyTransactions(page: number = 0, pageSize: number = 10): Promise<{ data?: VaultTransactionsResponse; error?: string }> {
    return this.request<VaultTransactionsResponse>('GET', `/api/players/me/money/transactions?page=${page}&pageSize=${pageSize}`);
  }

  async getMoneyTransactionsByUuid(uuid: string, page: number = 0, pageSize: number = 10): Promise<{ data?: VaultTransactionsResponse; error?: string; status?: number }> {
    return this.request<VaultTransactionsResponse>('GET', `/api/players/${uuid}/money/transactions?page=${page}&pageSize=${pageSize}`);
  }

  // ==================== CONSOLE / SERVERS ====================

  /**
   * Get list of game servers
   */
  async getServers(): Promise<{ data?: ServersResponse; error?: string }> {
    return this.request<ServersResponse>('GET', '/api/console/servers');
  }

  /**
   * Get the full (unfiltered) list of game servers.
   */
  async getAllServers(): Promise<{ data?: ServersResponse; error?: string }> {
    return this.request<ServersResponse>('GET', '/api/console/servers/all');
  }

  /**
   * Get server details by identifier
   */
  async getServer(identifier: string): Promise<{ data?: ServerDTO; error?: string; status?: number; code?: string }> {
    return this.request<ServerDTO>('GET', `/api/console/servers/${encodeURIComponent(identifier)}`);
  }

  // ---- Schematics (dossier mods-data/schematics d'un serveur, délégué au satellite) ----

  /** Liste les schematics d'un serveur. error (404) si le serveur n'a pas de dossier schematics. */
  async listSchematics(identifier: string): Promise<{ data?: SchematicsResponse; error?: string }> {
    return this.request<SchematicsResponse>('GET', `/api/console/servers/${encodeURIComponent(identifier)}/schematics`);
  }

  /** Télécharge un schematic (binaire) → Blob, ou null si erreur. */
  async downloadSchematic(identifier: string, name: string): Promise<Blob | null> {
    const token = authService.getToken();
    if (!token) return null;
    const path = `/api/console/servers/${encodeURIComponent(identifier)}/schematics/${encodeURIComponent(name)}/download`;
    const res = await fetch(`${this.baseFor(path)}${path}`, {
      cache: 'no-store',
      headers: {
        'Authorization': `Bearer ${token}`,
        ...contextHeaders(),
      },
    });
    if (!res.ok) return null;
    return res.blob();
  }

  /** Envoie (crée/écrase) un schematic sur le serveur. Corps binaire brut (streamé). */
  async uploadSchematic(identifier: string, name: string, file: Blob): Promise<{ ok: boolean; error?: string }> {
    const token = authService.getToken();
    if (!token) return { ok: false, error: 'Not authenticated' };
    const path = `/api/console/servers/${encodeURIComponent(identifier)}/schematics/${encodeURIComponent(name)}`;
    const res = await fetch(`${this.baseFor(path)}${path}`, {
      method: 'POST',
      cache: 'no-store',
      headers: {
        'Authorization': `Bearer ${token}`,
        ...contextHeaders(),
        'Content-Type': 'application/octet-stream',
      },
      body: file,
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const t = await res.text();
        if (t) { try { msg = (JSON.parse(t) as { message?: string }).message ?? t; } catch { msg = t; } }
      } catch { /* ignore */ }
      return { ok: false, error: msg };
    }
    return { ok: true };
  }

  /**
   * Demande un ticket pour ouvrir la console d'un serveur.
   *
   * À enchaîner avec {@link consoleStreamUrl} : une WebSocket de navigateur ne peut pas porter
   * d'en-tête Authorization, d'où ce ticket à usage unique obtenu par un appel HTTP authentifié.
   */
  async createConsoleTicket(identifier: string): Promise<{ data?: ConsoleTicket; error?: string; status?: number; code?: string }> {
    return this.request<ConsoleTicket>('POST', '/api/panel/console-ticket', { serverId: identifier });
  }

  /**
   * URL absolue de la socket console, EN DIRECT sur le backend api-staff (PAS via le proxy
   * Cloudflare) : la Pages Function plante en 500 sur l'upgrade WebSocket, alors qu'api-staff
   * repond bien 101, y compris en cross-origin (verifie au curl). Meme principe que le login,
   * qui tape aussi api-staff en direct.
   */
  consoleStreamUrl(ticket: string): string {
    return `wss://api-staff.be.recube.gg/api/console/stream?ticket=${encodeURIComponent(ticket)}`;
  }

  /**
   * Get list of restart reasons
   */
  async getRestartReasons(): Promise<{ data?: RestartReasonsResponse; error?: string }> {
    return this.request<RestartReasonsResponse>('GET', '/api/console/restart-reasons');
  }

  /**
   * Restart a server with a reason
   */
  async restartServer(identifier: string, reason: string): Promise<{ data?: RestartResponse; error?: string }> {
    return this.request<RestartResponse>('POST', `/api/console/servers/${encodeURIComponent(identifier)}/restart`, { reason });
  }

  /**
   * Broadcast a message to all players on a server
   */
  async broadcastMessage(identifier: string, message: string): Promise<{ data?: BroadcastResponse; error?: string }> {
    return this.request<BroadcastResponse>('POST', `/api/console/servers/${encodeURIComponent(identifier)}/broadcast`, { message });
  }

  // ==================== SCHEDULED MESSAGES ====================

  /**
   * Get scheduled messages for a server
   */
  async getScheduledMessages(identifier: string): Promise<{ data?: ScheduledMessagesResponse; error?: string }> {
    return this.request<ScheduledMessagesResponse>('GET', `/api/console/servers/${encodeURIComponent(identifier)}/scheduled-messages`);
  }

  /**
   * Create a scheduled message
   */
  async createScheduledMessage(identifier: string, message: string, intervalMinutes: number): Promise<{ data?: ScheduledMessageDTO; error?: string }> {
    return this.request<ScheduledMessageDTO>('POST', `/api/console/servers/${encodeURIComponent(identifier)}/scheduled-messages`, { message, intervalMinutes });
  }

  /**
   * Update a scheduled message
   */
  async updateScheduledMessage(identifier: string, messageId: string, message: string, intervalMinutes: number, enabled: boolean): Promise<{ data?: ScheduledMessageDTO; error?: string }> {
    return this.request<ScheduledMessageDTO>('PUT', `/api/console/servers/${encodeURIComponent(identifier)}/scheduled-messages/${encodeURIComponent(messageId)}`, { message, intervalMinutes, enabled });
  }

  /**
   * Toggle a scheduled message
   */
  async toggleScheduledMessage(identifier: string, messageId: string, enabled: boolean): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request<{ success: boolean; message: string }>('PATCH', `/api/console/servers/${encodeURIComponent(identifier)}/scheduled-messages/${encodeURIComponent(messageId)}/toggle`, { enabled });
  }

  /**
   * Delete a scheduled message
   */
  async deleteScheduledMessage(identifier: string, messageId: string): Promise<{ data?: void; error?: string }> {
    return this.request<void>('DELETE', `/api/console/servers/${encodeURIComponent(identifier)}/scheduled-messages/${encodeURIComponent(messageId)}`);
  }

  // ==================== CONFIGURATION / BANWORDS ====================

  /**
   * Get list of banwords
   */
  async getBanwords(): Promise<{ data?: BanwordsResponse; error?: string }> {
    return this.request<BanwordsResponse>('GET', '/api/config/banwords');
  }

  /**
   * Create a new banword
   */
  async createBanword(word: string, regex: boolean = false): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request<{ success: boolean; message: string }>('POST', '/api/config/banwords', { word, regex });
  }

  /**
   * Delete a banword
   */
  async deleteBanword(word: string): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request<{ success: boolean; message: string }>('DELETE', `/api/config/banwords/${encodeURIComponent(word)}`);
  }

  // ==================== CONFIGURATION / WHITELIST ====================

  /**
   * Get whitelist entries
   */
  async getWhitelist(): Promise<{ data?: WhitelistResponse; error?: string }> {
    return this.request<WhitelistResponse>('GET', '/api/config/whitelist');
  }

  /**
   * Add user to whitelist
   */
  async addToWhitelist(name: string): Promise<{ data?: WhitelistEntryDTO; error?: string }> {
    return this.request<WhitelistEntryDTO>('POST', '/api/config/whitelist', { name });
  }

  /**
   * Remove user from whitelist
   */
  async removeFromWhitelist(name: string): Promise<{ data?: WhitelistChangeDTO; error?: string }> {
    return this.request<WhitelistChangeDTO>('DELETE', `/api/config/whitelist/${encodeURIComponent(name)}`);
  }

  /**
   * Retire tous les joueurs de la liste du réseau ; son état (allumée ou éteinte) reste.
   */
  async clearWhitelist(): Promise<{ data?: WhitelistChangeDTO; error?: string }> {
    return this.request<WhitelistChangeDTO>('DELETE', '/api/config/whitelist');
  }

  /** Liste du réseau allumée ou éteinte. */
  async getWhitelistStatus(): Promise<{ data?: WhitelistStatusDTO; error?: string; status?: number }> {
    return this.request<WhitelistStatusDTO>('GET', '/api/config/whitelist/status');
  }

  /** Allume ou éteint la liste du réseau, comme /whitelistall on|off. */
  async setWhitelistStatus(enabled: boolean): Promise<{ data?: WhitelistStatusDTO; error?: string; status?: number }> {
    return this.request<WhitelistStatusDTO>('PUT', '/api/config/whitelist/status', { enabled });
  }

  /** Liste blanche de chaque serveur de jeu du groupe. */
  async getServerWhitelists(): Promise<{ data?: { servers: ServerWhitelistDTO[]; total: number }; error?: string; status?: number }> {
    return this.request<{ servers: ServerWhitelistDTO[]; total: number }>('GET', '/api/config/whitelist/servers');
  }

  /** Allume ou éteint la liste blanche d'un serveur, comme /whitelist on|off. */
  async setServerWhitelistStatus(identifier: string, enabled: boolean): Promise<{ data?: ServerWhitelistActionDTO; error?: string; status?: number }> {
    return this.request<ServerWhitelistActionDTO>('PUT', `/api/config/whitelist/servers/${encodeURIComponent(identifier)}/status`, { enabled });
  }

  async addServerWhitelistName(identifier: string, name: string): Promise<{ data?: ServerWhitelistActionDTO; error?: string; status?: number }> {
    return this.request<ServerWhitelistActionDTO>('POST', `/api/config/whitelist/servers/${encodeURIComponent(identifier)}/names`, { name });
  }

  async removeServerWhitelistName(identifier: string, name: string): Promise<{ data?: ServerWhitelistActionDTO; error?: string; status?: number }> {
    return this.request<ServerWhitelistActionDTO>('DELETE',
      `/api/config/whitelist/servers/${encodeURIComponent(identifier)}/names/${encodeURIComponent(name)}`);
  }

  // ==================== Mondes (NationsGlory) ====================
  // Module « worlds » du satellite NG : carte des pays, géo-régions, niveaux, marqueurs de biomes,
  // import NG Java et synchro du roster. Les appels typés vivent dans pages/config/worlds/worlds.api.ts ;
  // ici, seulement le transport (JSON générique et grille binaire).

  /** Appel JSON sur /api/config/worlds{sub}. `status` permet de réagir à un 409 (forcer, remplacer). */
  async worldsRequest<T>(method: string, sub: string, body?: unknown): Promise<{ data?: T; error?: string; status?: number }> {
    return this.request<T>(method, `/api/config/worlds${sub}`, body);
  }

  /**
   * Grille d'un niveau au format RWG1 (octets gzip, décodés par la page). `since` = version locale :
   * 0 donne la grille complète, sinon le delta des blocs modifiés depuis.
   */
  async getWorldsGrid(level: string, since: number): Promise<{ data?: ArrayBuffer; error?: string }> {
    const token = authService.getToken();
    if (!token) return { error: 'Not authenticated' };
    const path = `/api/config/worlds/levels/${encodeURIComponent(level)}/grid?since=${since}`;
    try {
      const res = await fetch(`${this.baseFor(path)}${path}`, {
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${token}`,
          ...contextHeaders(),
          'Accept': 'application/octet-stream, application/json',
        },
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        if (res.status === 403) msg = 'Permission denied';
        try {
          const t = await res.text();
          if (t) { try { msg = (JSON.parse(t) as { message?: string }).message ?? msg; } catch { /* corps non JSON */ } }
        } catch { /* ignore */ }
        return { error: msg };
      }
      return { data: await res.arrayBuffer() };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Request failed' };
    }
  }

  // ==================== STATISTICS ====================

  /**
   * Get current economy stats (live total money)
   */
  /**
   * Section « Grades actifs » : joueurs qui portent un grade vendu, fin et vraie fin de chaque grade
   * (LuckPerms du projet). Satellite qui porte cette section seulement ; avant, 404.
   */
  async getActiveGrades(grades?: string[]): Promise<{ data?: ActiveGradesResponse; error?: string; status?: number }> {
    const q = grades && grades.length ? `?grades=${encodeURIComponent(grades.join(','))}` : '';
    return this.request<ActiveGradesResponse>('GET', `/api/stats/grades${q}`);
  }

  /** Aperçu de la correction des fins de grade écrites en millisecondes (admins du panel). Rien n'est écrit. */
  async getGradeRepairPreview(): Promise<{ data?: GradeRepairPreview; error?: string; status?: number }> {
    return this.request<GradeRepairPreview>('GET', '/api/stats/grades/repair');
  }

  /**
   * Applique la correction relue dans l'aperçu `fingerprint` (admins du panel). Refusée (409) si la
   * liste a changé depuis l'aperçu : rien n'est alors écrit.
   */
  async applyGradeRepair(fingerprint: string): Promise<{ data?: GradeRepairResult; error?: string; status?: number }> {
    return this.request<GradeRepairResult>('POST', '/api/stats/grades/repair', { fingerprint, confirm: true });
  }

  /**
   * Rapprochement des grades dus par les achats Tebex avec LuckPerms (admins du panel). Le corps ne
   * porte AUCUNE donnée personnelle : il est calculé dans le navigateur depuis l'export nettoyé
   * (cf pages/stats/tebexImport.ts). Rien n'est écrit.
   */
  async previewTebexGrants(players: TebexGrantPlayerDTO[], replaceLifetime: boolean): Promise<{ data?: TebexGrantPreview; error?: string; status?: number }> {
    return this.request<TebexGrantPreview>('POST', '/api/stats/grades/repair/tebex', { players, replaceLifetime });
  }

  /** Pose les grades montrés par l'aperçu `fingerprint` (admins du panel). 409 si la liste a changé. */
  async applyTebexGrants(players: TebexGrantPlayerDTO[], replaceLifetime: boolean, fingerprint: string): Promise<{ data?: TebexGrantResult; error?: string; status?: number }> {
    return this.request<TebexGrantResult>('POST', '/api/stats/grades/repair/tebex/apply', { players, replaceLifetime, fingerprint, confirm: true });
  }

  /** Journal des dernières corrections, ancienne fin de chaque node comprise (admins du panel). */
  async getGradeRepairRuns(): Promise<{ data?: GradeRepairRunsResponse; error?: string; status?: number }> {
    return this.request<GradeRepairRunsResponse>('GET', '/api/stats/grades/repair/runs');
  }

  /** Répartition quotidienne de l'XP gagnée par métier (snapshots quotidiens, delta). */
  async getJobXpBreakdown(days: number = 30): Promise<{ data?: JobXpBreakdownResponse; error?: string }> {
    return this.request<JobXpBreakdownResponse>('GET', `/api/stats/gameplay/jobs?days=${days}`);
  }

  /** Métiers : joueurs et XP par métier, boosts, quêtes du jour, état du relevé et du journal des méthodes. */
  async getJobsOverview(): Promise<{ data?: JobsOverviewResponse; error?: string; status?: number }> {
    return this.request<JobsOverviewResponse>('GET', '/api/stats/gameplay/jobs/overview');
  }

  /** Joueurs par niveau ; `curveQuery` = paramètres `curve=métier:t1,t2...` (cf. pages/stats/jobStats.curveParams). */
  async getJobLevels(curveQuery: string): Promise<{ data?: JobLevelsResponse; error?: string; status?: number }> {
    return this.request<JobLevelsResponse>('GET', `/api/stats/gameplay/jobs/levels${curveQuery ? `?${curveQuery}` : ''}`);
  }

  /** Classement de l'XP, tous métiers (`job` absent) ou un métier, avec recherche. */
  async getJobLeaderboard(
    job: string | null,
    page: number,
    size: number,
    query?: string,
  ): Promise<{ data?: JobTopResponse; error?: string; status?: number }> {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (job) params.set('job', job);
    const q = query?.trim();
    if (q) params.set('q', q);
    return this.request<JobTopResponse>('GET', `/api/stats/gameplay/jobs/top?${params.toString()}`);
  }

  /** Tendances des métiers (relevé horaire du satellite). */
  async getJobTrends(days: number): Promise<{ data?: JobTrendsResponse; error?: string; status?: number }> {
    return this.request<JobTrendsResponse>('GET', `/api/stats/gameplay/jobs/trends?days=${days}`);
  }

  /** Joueurs qui ont gagné le plus d'XP sur la période. */
  async getJobGainers(days: number, job: string | null, limit = 20): Promise<{ data?: JobGainersResponse; error?: string; status?: number }> {
    const params = new URLSearchParams({ days: String(days), limit: String(limit) });
    if (job) params.set('job', job);
    return this.request<JobGainersResponse>('GET', `/api/stats/gameplay/jobs/gainers?${params.toString()}`);
  }

  /** Méthodes : XP par type d'action et par cible (journal des mods). */
  async getJobMethods(days: number, job: string | null): Promise<{ data?: JobMethodsResponse; error?: string; status?: number }> {
    const params = new URLSearchParams({ days: String(days) });
    if (job) params.set('job', job);
    return this.request<JobMethodsResponse>('GET', `/api/stats/gameplay/jobs/methods?${params.toString()}`);
  }

  /** Admin shop (Paladium) : dépenses, gains, objets les plus achetés et vendus, joueurs. */
  async getAdminShopStats(days: number): Promise<{ data?: AdminShopResponse; error?: string; status?: number }> {
    return this.request<AdminShopResponse>('GET', `/api/stats/economy/adminshop?days=${days}`);
  }

  /** Box et lootbox : ouvertures, joueurs, lots, légendaires (taux vu et prévu) ; `box` : une seule box. */
  async getBoxStats(days: number, box?: string | null): Promise<{ data?: BoxStatsResponse; error?: string; status?: number }> {
    const q = box ? `&box=${encodeURIComponent(box)}` : '';
    return this.request<BoxStatsResponse>('GET', `/api/stats/economy/box?days=${days}${q}`);
  }

  /**
   * Boutique en jeu (satellite 1.0.33) : achats, acheteurs, points dépensés et rendus, par produit et par jour, plus
   * gros acheteurs et derniers achats, sur les `days` derniers jours. 404 face à un satellite plus ancien.
   */
  async getShopStats(days: number): Promise<{ data?: ShopStatsResponse; error?: string; status?: number }> {
    return this.request<ShopStatsResponse>('GET', `/api/stats/storepoints/shop?days=${days}`);
  }

  /**
   * Mouvements des points boutique (satellite 1.0.34) : créés par source, dépensés par destination (boutique, market,
   * échanges...), retraits hors dépense et soldes remis, sur les `days` derniers jours. 404 face à un satellite plus ancien.
   */
  async getStorePointFlows(days: number, type: 'seasonal' | 'classic' | 'all'): Promise<{ data?: StorePointFlowsResponse; error?: string; status?: number }> {
    return this.request<StorePointFlowsResponse>('GET', `/api/stats/storepoints/flows?days=${days}&type=${type}`);
  }

  /**
   * Personas des joueurs (satellite 1.0.31) : une page de la liste (recherche `query`, tri par la part de `persona`),
   * la répartition globale et l'état des indicateurs, sur les `days` derniers jours.
   */
  async getPersonas(
    days: number,
    page: number,
    size: number,
    query?: string,
    persona?: string | null,
  ): Promise<{ data?: PersonasResponse; error?: string; status?: number }> {
    const params = new URLSearchParams({ days: String(days), page: String(page), size: String(size) });
    const q = query?.trim();
    if (q) params.set('q', q);
    if (persona) params.set('persona', persona);
    return this.request<PersonasResponse>('GET', `/api/stats/gameplay/personas?${params.toString()}`);
  }

  /** Fiche des personas d'un joueur : parts, scores et indicateurs qui les expliquent. */
  async getPlayerPersonas(uuid: string, days: number): Promise<{ data?: PlayerPersonasResponse; error?: string; status?: number }> {
    return this.request<PlayerPersonasResponse>('GET', `/api/stats/gameplay/personas/player/${encodeURIComponent(uuid)}?days=${days}`);
  }

  /** Factions (Paladium) : nombre, membres, arbre de techno, banque, ligues, historique. */
  async getFactionStats(): Promise<{ data?: FactionsResponse; error?: string; status?: number }> {
    return this.request<FactionsResponse>('GET', '/api/stats/gameplay/factions');
  }

  /**
   * Pays (NationsGlory) du satellite d'un groupe : pays fondés et libres, membres, power, territoire, banque, PIB,
   * R&D, guerres, historique. `gameGroup` impose le groupe (comparaison Nova et Iris) ; omis = groupe actif.
   */
  async getCountryStats(gameGroup?: string | null): Promise<{ data?: CountriesResponse; error?: string; status?: number }> {
    return this.request<CountriesResponse>('GET', '/api/stats/gameplay/countries', undefined, gameGroup ? { gameGroup } : undefined);
  }

  /** Plateformes et modes de jeu des joueurs, en ligne et sur la période. */
  async getPlatformStats(days: number): Promise<{ data?: PlatformsResponse; error?: string; status?: number }> {
    return this.request<PlatformsResponse>('GET', `/api/stats/players/platforms?days=${days}`);
  }

  // ---------- Onboarding (satellite 1.0.35) ----------
  // `gameGroup` : groupe du satellite ÉCRIVAIN, imposé à la requête (NationsGlory : celui qui annonce la capacité
  // « onboarding ») ; null ou omis = appel normal (Paladium, qui n'envoie jamais de groupe).

  /** Chemin d'une route de l'onboarding avec ses filtres (les vides sont omis). */
  private onboardingPath(route: string, q?: OnboardingQuery, extra?: Record<string, string>): string {
    const params = new URLSearchParams();
    if (q) {
      params.set('startDate', q.startDate);
      params.set('endDate', q.endDate);
      if (q.group) params.set('group', q.group);
      if (q.flags) params.set('flags', q.flags);
      if (q.focus) params.set('focus', q.focus);
    }
    for (const [k, v] of Object.entries(extra ?? {})) params.set(k, v);
    const qs = params.toString();
    return `/api/stats/onboarding/${route}${qs ? `?${qs}` : ''}`;
  }

  private onboardingOptions(gameGroup?: string | null): RequestOptions | undefined {
    return gameGroup ? { gameGroup } : undefined;
  }

  /** Puces, colonnes, groupes et dates du suivi. 404 face à un satellite plus ancien que la 1.0.35. */
  async getOnboardingMeta(gameGroup?: string | null): Promise<{ data?: OnboardingMetaResponse; error?: string; status?: number }> {
    return this.request<OnboardingMetaResponse>('GET', this.onboardingPath('meta'), undefined, this.onboardingOptions(gameGroup));
  }

  /** Les cinq cartes et leur variation face à la même période un an avant. */
  async getOnboardingOverview(q: OnboardingQuery, gameGroup?: string | null): Promise<{ data?: OnboardingOverviewResponse; error?: string; status?: number }> {
    return this.request<OnboardingOverviewResponse>('GET', this.onboardingPath('overview', q), undefined, this.onboardingOptions(gameGroup));
  }

  /** Inscriptions par jour, semaine ou mois ; `compare` ajoute la période un an avant. */
  async getOnboardingRegistrations(
    q: OnboardingQuery,
    period: 'day' | 'week' | 'month',
    compare: boolean,
    gameGroup?: string | null,
  ): Promise<{ data?: OnboardingRegistrationsResponse; error?: string; status?: number }> {
    return this.request<OnboardingRegistrationsResponse>('GET',
      this.onboardingPath('registrations', q, { period, compare: String(compare) }), undefined, this.onboardingOptions(gameGroup));
  }

  /** Parcours d'accès (nœuds du sankey), sur l'échantillon `q.focus`. */
  async getOnboardingFunnel(q: OnboardingQuery, gameGroup?: string | null): Promise<{ data?: OnboardingFunnelResponse; error?: string; status?: number }> {
    return this.request<OnboardingFunnelResponse>('GET', this.onboardingPath('funnel-tree', q), undefined, this.onboardingOptions(gameGroup));
  }

  /** Étapes du tutoriel d'accueil, sur l'échantillon `q.focus`. */
  async getOnboardingTutorial(q: OnboardingQuery, gameGroup?: string | null): Promise<{ data?: OnboardingTutorialResponse; error?: string; status?: number }> {
    return this.request<OnboardingTutorialResponse>('GET', this.onboardingPath('tutorial-steps', q), undefined, this.onboardingOptions(gameGroup));
  }

  /** Parcours d'intégration par groupe (toutes les lignes, sans filtre de groupe). */
  async getOnboardingGroupAccess(q: OnboardingQuery, gameGroup?: string | null): Promise<{ data?: OnboardingAccessResponse; error?: string; status?: number }> {
    return this.request<OnboardingAccessResponse>('GET', this.onboardingPath('group-access', { ...q, group: null }), undefined, this.onboardingOptions(gameGroup));
  }

  /** Parcours de conversion par groupe (toutes les lignes, sans filtre de groupe). */
  async getOnboardingGroupConversion(q: OnboardingQuery, gameGroup?: string | null): Promise<{ data?: OnboardingConversionResponse; error?: string; status?: number }> {
    return this.request<OnboardingConversionResponse>('GET', this.onboardingPath('group-conversion', { ...q, group: null }), undefined, this.onboardingOptions(gameGroup));
  }

  /** Distribution du temps de jeu des inscrits ; `compare` ajoute la période un an avant. */
  async getOnboardingDistribution(
    q: OnboardingQuery,
    compare: boolean,
    gameGroup?: string | null,
  ): Promise<{ data?: OnboardingDistributionResponse; error?: string; status?: number }> {
    return this.request<OnboardingDistributionResponse>('GET',
      this.onboardingPath('playtime-distribution', q, { compare: String(compare) }), undefined, this.onboardingOptions(gameGroup));
  }

  async getEconomyStats(): Promise<{ data?: EconomyCurrentResponse; error?: string }> {
    return this.request<EconomyCurrentResponse>('GET', '/api/stats/economy/current');
  }

  /**
   * Get economy history for chart
   */
  async getEconomyHistory(days: number = 30): Promise<{ data?: EconomyHistoryResponse; error?: string }> {
    return this.request<EconomyHistoryResponse>('GET', `/api/stats/economy/history?days=${days}`);
  }

  /**
   * Get economy breakdown (median + active/dormant money)
   */
  async getEconomyBreakdown(): Promise<{ data?: EconomyBreakdownResponse; error?: string }> {
    return this.request<EconomyBreakdownResponse>('GET', '/api/stats/economy/breakdown');
  }

  /**
   * Classement de l'argent : une page des comptes du plus riche au moins riche (page 0 en tête), ou les
   * joueurs que `query` trouve (pseudo Recube ou gamertag par le début, uuid entier), avec leur rang.
   */
  async getMoneyLeaderboard(
    page: number,
    size: number,
    query?: string,
  ): Promise<{ data?: MoneyLeaderboardResponse; error?: string; status?: number }> {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    const q = query?.trim();
    if (q) params.set('q', q);
    return this.request<MoneyLeaderboardResponse>('GET', `/api/stats/economy/top?${params.toString()}`);
  }

  /**
   * Trigger manual economy snapshot (admin)
   */
  async triggerEconomySnapshot(): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request<{ success: boolean; message: string }>('POST', '/api/stats/economy/snapshot');
  }

  // ==================== STORE POINTS STATISTICS ====================

  /**
   * Get current store points stats (live total points)
   */
  async getStorePointsStats(): Promise<{ data?: StorePointsCurrentResponse; error?: string }> {
    return this.request<StorePointsCurrentResponse>('GET', '/api/stats/storepoints/current');
  }

  /**
   * Get store points history for chart
   */
  async getStorePointsHistory(days: number = 30): Promise<{ data?: StorePointsHistoryResponse; error?: string }> {
    return this.request<StorePointsHistoryResponse>('GET', `/api/stats/storepoints/history?days=${days}`);
  }

  /**
   * Trigger manual store points snapshot (admin)
   */
  async triggerStorePointsSnapshot(): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request<{ success: boolean; message: string }>('POST', '/api/stats/storepoints/snapshot');
  }

  // ==================== TRANSACTION STATS ====================

  /**
   * Get vault (money) transaction stats by day and category
   * @param startDate Start date (YYYY-MM-DD)
   * @param endDate End date (YYYY-MM-DD)
   * @param type Transaction type: 'give', 'clear', or undefined for all
   */
  async getVaultTransactionStats(
    startDate?: string,
    endDate?: string,
    type?: 'give' | 'clear',
    reason?: string
  ): Promise<{ data?: TransactionStatsResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.set('startDate', startDate);
    if (endDate) queryParams.set('endDate', endDate);
    if (type) queryParams.set('type', type);
    if (reason) queryParams.set('reason', reason);

    const queryString = queryParams.toString();
    const path = `/api/stats/transactions/vault${queryString ? `?${queryString}` : ''}`;

    return this.request<TransactionStatsResponse>('GET', path);
  }

  /**
   * Get store point transaction stats by day and category
   */
  async getStorePointTransactionStats(
    startDate?: string,
    endDate?: string,
    type?: 'give' | 'clear'
  ): Promise<{ data?: TransactionStatsResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.set('startDate', startDate);
    if (endDate) queryParams.set('endDate', endDate);
    if (type) queryParams.set('type', type);

    const queryString = queryParams.toString();
    const path = `/api/stats/transactions/storepoints${queryString ? `?${queryString}` : ''}`;

    return this.request<TransactionStatsResponse>('GET', path);
  }

  /**
   * L'argent créé, détruit et échangé entre joueurs, par jour et par catégorie, entre deux jours inclus
   * (YYYY-MM-DD). Sans dates : les sept derniers jours.
   */
  async getVaultFlowStats(
    startDate?: string,
    endDate?: string
  ): Promise<{ data?: VaultFlowStatsResponse; error?: string; status?: number }> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.set('startDate', startDate);
    if (endDate) queryParams.set('endDate', endDate);

    const queryString = queryParams.toString();
    return this.request<VaultFlowStatsResponse>('GET', `/api/stats/transactions/vault/flows${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get vault transaction reasons summary (for analysis)
   */
  async getVaultReasonsSummary(
    startDate?: string,
    endDate?: string
  ): Promise<{ data?: ReasonsSummaryResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.set('startDate', startDate);
    if (endDate) queryParams.set('endDate', endDate);

    const queryString = queryParams.toString();
    const path = `/api/stats/transactions/vault/reasons${queryString ? `?${queryString}` : ''}`;

    return this.request<ReasonsSummaryResponse>('GET', path);
  }

  // ==================== LOGS ====================

  /**
   * Get activity logs
   */
  async getLogs(params: LogsParams = {}): Promise<{ data?: LogsResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.set('page', params.page.toString());
    if (params.size !== undefined) queryParams.set('size', params.size.toString());
    if (params.username) queryParams.set('username', params.username);
    if (params.method) queryParams.set('method', params.method);
    if (params.uri) queryParams.set('uri', params.uri);
    if (params.statusCode !== undefined) queryParams.set('statusCode', params.statusCode.toString());
    if (params.from) queryParams.set('from', params.from);
    if (params.to) queryParams.set('to', params.to);

    const queryString = queryParams.toString();
    const path = `/api/logs${queryString ? `?${queryString}` : ''}`;

    return this.request<LogsResponse>('GET', path);
  }

  /**
   * Get logs statistics
   */
  async getLogsStats(): Promise<{ data?: LogsStatsResponse; error?: string }> {
    return this.request<LogsStatsResponse>('GET', '/api/logs/stats');
  }

  /**
   * Check if user has panel access permission
   */
  async checkPanelAccess(): Promise<{
    data?: AccessCheckResponse;
    error?: string;
    status?: number;
    /** Code d'erreur de l'API (ex. GRADE_BACKEND_UNAVAILABLE). */
    code?: string;
    /** Identifiant que compare l'accès d'urgence, renvoyé dans le corps du 503 des grades. */
    emergencyId?: string;
  }> {
    const token = authService.getToken();

    if (!token) {
      return { error: 'Not authenticated', status: 401 };
    }

    try {
      // Auth = backend PRINCIPAL partagé (jamais l'API du jeu actif : /api-ng n'est pas proxifié → SPA HTML).
      const response = await fetch(`${this.mainBaseUrl}/api/auth/check-access`, {
        method: 'GET',
        cache: 'no-store', // jamais de réponse d'API mise en cache (sinon un vieux fallback SPA HTML pourrait être servi)
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Project': useGameStore.getState().activeGameId,
        },
      });

      if (response.status === 403) {
        return { error: 'Access denied', status: 403 };
      }

      if (response.status === 401) {
        return { error: 'Invalid token', status: 401 };
      }

      if (!response.ok) {
        // 503 de la base des grades : le corps porte l'identifiant que compare l'accès d'urgence
        // (claim xuid du jeton, jamais un secret). Corps vide ou HTML de proxy : ignoré.
        let code: string | undefined;
        let emergencyId: string | undefined;
        try {
          const body = JSON.parse(await response.text()) as { code?: unknown; emergencyId?: unknown } | null;
          if (typeof body?.code === 'string') code = body.code;
          if (typeof body?.emergencyId === 'string' && body.emergencyId) emergencyId = body.emergencyId;
        } catch {
          // pas du JSON
        }
        return { error: `HTTP ${response.status}`, status: response.status, code, emergencyId };
      }

      // Le corps DOIT être du JSON. Si on reçoit du HTML (fallback SPA en cache, page d'erreur
      // passerelle…), ne pas planter sur response.json() : renvoyer une erreur claire.
      const text = await response.text();
      try {
        const data = text ? JSON.parse(text) : null;
        return { data: (data ?? undefined) as AccessCheckResponse | undefined, status: 200 };
      } catch {
        return { error: 'Réponse inattendue du serveur (réessaie / vide le cache).', status: response.status };
      }
    } catch (err) {
      console.error('API request failed:', err);
      return { error: err instanceof Error ? err.message : 'Request failed' };
    }
  }

  // ==================== ONLINE PLAYERS ====================

  /**
   * Get online players summary (total, slots, by server)
   */
  async getOnlineSummary(): Promise<{ data?: OnlineSummaryResponse; error?: string }> {
    return this.request<OnlineSummaryResponse>('GET', '/api/online/summary');
  }

  /**
   * Get all online players with optional filters
   */
  async getOnlinePlayers(params?: { server?: string; search?: string }): Promise<{ data?: OnlinePlayersListResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (params?.server) queryParams.set('server', params.server);
    if (params?.search) queryParams.set('search', params.search);
    const queryString = queryParams.toString();
    const path = `/api/online/players${queryString ? `?${queryString}` : ''}`;
    return this.request<OnlinePlayersListResponse>('GET', path);
  }

  /**
   * Get online staff members
   */
  async getOnlineStaff(): Promise<{ data?: OnlinePlayersListResponse; error?: string }> {
    return this.request<OnlinePlayersListResponse>('GET', '/api/online/staff');
  }

  /**
   * Get active proxies
   */
  async getOnlineProxies(): Promise<{ data?: ProxiesListResponse; error?: string }> {
    return this.request<ProxiesListResponse>('GET', '/api/online/proxies');
  }

  /**
   * Get player count by server
   */
  async getOnlineServers(): Promise<{ data?: ServerStatsListResponse; error?: string }> {
    return this.request<ServerStatsListResponse>('GET', '/api/online/servers');
  }

  /**
   * Check if a player is online by UUID or name
   */
  async checkPlayerOnline(identifier: string): Promise<{ data?: OnlineCheckResponse; error?: string }> {
    return this.request<OnlineCheckResponse>('GET', `/api/online/check/${encodeURIComponent(identifier)}`);
  }

  // ========================
  // Player Stats endpoints
  // ========================

  /**
   * Get player statistics for a given duration
   * @param durationMinutes Duration in minutes (10, 30, 60, 180, 1440, 4320, 10080, 20160, 43200, 86400, 129600, 262800, 525600)
   */
  async getPlayerStats(durationMinutes: number): Promise<{ data?: PlayerStatsResponse; error?: string }> {
    return this.request<PlayerStatsResponse>('GET', `/api/stats/players?duration=${durationMinutes}`);
  }

  /**
   * Get valid durations for player stats
   */
  async getPlayerStatsDurations(): Promise<{ data?: { durations: number[] }; error?: string }> {
    return this.request<{ durations: number[] }>('GET', '/api/stats/players/durations');
  }

  /**
   * Get player activity (new/unique players + total playtime) with previous-period comparison.
   * Pass either `days` (preset) or a custom startDate/endDate (YYYY-MM-DD).
   */
  async getPlayerActivity(
    days?: number,
    startDate?: string,
    endDate?: string
  ): Promise<{ data?: PlayerActivityResponse; error?: string }> {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.set('startDate', startDate);
      params.set('endDate', endDate);
    } else if (days) {
      params.set('days', String(days));
    }
    const qs = params.toString();
    return this.request<PlayerActivityResponse>('GET', `/api/stats/players/activity${qs ? `?${qs}` : ''}`);
  }

  /** Nombre total de joueurs uniques connus (roster complet, tous temps confondus). */
  async getTotalPlayers(): Promise<{ data?: { total: number }; error?: string }> {
    return this.request<{ total: number }>('GET', '/api/stats/players/activity/total');
  }

  /**
   * Daily player activity series (new/unique players + playtime) for charts.
   */
  async getPlayerActivitySeries(
    days?: number,
    startDate?: string,
    endDate?: string
  ): Promise<{ data?: PlayerActivitySeriesResponse; error?: string }> {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.set('startDate', startDate);
      params.set('endDate', endDate);
    } else if (days) {
      params.set('days', String(days));
    }
    const qs = params.toString();
    return this.request<PlayerActivitySeriesResponse>('GET', `/api/stats/players/activity/series${qs ? `?${qs}` : ''}`);
  }

  // ==================== SERVER LOGS HISTORY ====================

  /**
   * Get historical logs for a server on a specific date
   * @param identifier Server identifier
   * @param date Date in YYYY-MM-DD format
   * @param search Optional search query
   * @param from Offset for pagination (default: 0)
   * @param size Number of results (default: 1000, max: 10000)
   */
  async getServerLogsHistory(
    identifier: string,
    date?: string,
    search?: string,
    from: number = 0,
    size: number = 1000
  ): Promise<{ data?: ServerLogsHistoryResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (date) queryParams.set('date', date);
    if (search) queryParams.set('search', search);
    queryParams.set('from', from.toString());
    queryParams.set('size', size.toString());

    const queryString = queryParams.toString();
    return this.request<ServerLogsHistoryResponse>(
      'GET',
      `/api/servers/${encodeURIComponent(identifier)}/logs/history?${queryString}`
    );
  }

  /**
   * Get available log dates for a server
   * @param identifier Server identifier
   */
  async getServerLogsDates(identifier: string): Promise<{ data?: ServerLogsDatesResponse; error?: string }> {
    return this.request<ServerLogsDatesResponse>('GET', `/api/servers/${encodeURIComponent(identifier)}/logs/dates`);
  }

  /**
   * Get logs collector status (monitoring)
   */
  async getLogsCollectorStatus(): Promise<{ data?: LogsCollectorStatusResponse; error?: string }> {
    return this.request<LogsCollectorStatusResponse>('GET', '/api/servers/logs/collector/status');
  }

  // ==================== RANK MANAGEMENT ====================
  // Chaque appel accepte `options.gameGroup` : le groupe de jeu (LuckPerms de ce groupe) visé par
  // CETTE requête, quel que soit le groupe actif (page Grades : un cadre par groupe ; « Créer aussi
  // sur »). Omis = groupe actif du jeu, comme avant ; ignoré pour un jeu sans groupes.

  /** Get all available groups/ranks (LuckPerms du groupe de jeu imposé, sinon du groupe actif). */
  async getAllGroups(options?: RequestOptions): Promise<{ data?: GroupsResponse; error?: string; status?: number; code?: string }> {
    return this.request<GroupsResponse>('GET', '/api/ranks/groups', undefined, options);
  }

  /**
   * Get groups that the current staff can manage (has recube.allow-rank.{groupname})
   */
  async getManageableGroups(options?: RequestOptions): Promise<{ data?: GroupsResponse; error?: string; status?: number; code?: string }> {
    return this.request<GroupsResponse>('GET', '/api/ranks/manageable', undefined, options);
  }

  /**
   * Get a player's current groups/ranks
   */
  async getPlayerGroups(uuid: string, options?: RequestOptions): Promise<{ data?: PlayerGroupsResponse; error?: string; status?: number; code?: string }> {
    return this.request<PlayerGroupsResponse>('GET', `/api/ranks/player/${encodeURIComponent(uuid)}`, undefined, options);
  }

  /**
   * Add a group/rank to a player
   */
  async addGroupToPlayer(uuid: string, groupName: string, options?: RequestOptions): Promise<{ data?: RankSuccessResponse; error?: string; status?: number; code?: string }> {
    return this.request<RankSuccessResponse>('POST', `/api/ranks/player/${encodeURIComponent(uuid)}`, { groupName }, options);
  }

  /**
   * Remove a group/rank from a player
   */
  async removeGroupFromPlayer(uuid: string, groupName: string, options?: RequestOptions): Promise<{ data?: RankSuccessResponse; error?: string; status?: number; code?: string }> {
    return this.request<RankSuccessResponse>('DELETE', `/api/ranks/player/${encodeURIComponent(uuid)}/${encodeURIComponent(groupName)}`, undefined, options);
  }

  /**
   * Joueurs qui portent directement un grade (LuckPerms du groupe, sans base de joueurs) : onglet
   * Joueurs d'un grade. BE-Panel-Games 1.0.15 ou plus ; avant, 404.
   */
  async getGroupMembers(name: string, options?: RequestOptions): Promise<{ data?: GroupMembersResponse; error?: string; status?: number; code?: string }> {
    return this.request<GroupMembersResponse>('GET', `/api/ranks/groups/${encodeURIComponent(name)}/members`, undefined, options);
  }

  /**
   * Joueur désigné par un uuid, un pseudo Recube ou un gamertag, trouvé sans base de joueurs (joueurs
   * en ligne, ProfileAPI, LuckPerms du groupe) : pour lui donner ou retirer un grade. BE-Panel-Games
   * 1.0.15 ou plus ; avant, 404 sans motif.
   */
  async lookupRankPlayer(name: string, options?: RequestOptions): Promise<{ data?: RankPlayerLookupDTO; error?: string; status?: number; code?: string }> {
    return this.request<RankPlayerLookupDTO>('GET', `/api/ranks/lookup?name=${encodeURIComponent(name)}`, undefined, options);
  }

  // ==================== GRADE MANAGEMENT ====================

  /** Crée un nouveau grade (groupe LuckPerms). */
  async createGroup(name: string, options?: RequestOptions): Promise<{ data?: RankSuccessResponse; error?: string; status?: number; code?: string }> {
    return this.request<RankSuccessResponse>('POST', '/api/ranks/groups', { name }, options);
  }

  /** Supprime un grade. */
  async deleteGroup(name: string, options?: RequestOptions): Promise<{ data?: RankSuccessResponse; error?: string; status?: number; code?: string }> {
    return this.request<RankSuccessResponse>('DELETE', `/api/ranks/groups/${encodeURIComponent(name)}`, undefined, options);
  }

  /** Liste les permissions (nodes) d'un grade. */
  async getGroupPermissions(name: string, options?: RequestOptions): Promise<{ data?: GroupPermissionsResponse; error?: string; status?: number; code?: string }> {
    return this.request<GroupPermissionsResponse>('GET', `/api/ranks/groups/${encodeURIComponent(name)}/permissions`, undefined, options);
  }

  /** Ajoute une permission à un grade. */
  async addGroupPermission(name: string, permission: string, value: boolean = true, options?: RequestOptions): Promise<{ data?: RankSuccessResponse; error?: string; status?: number; code?: string }> {
    return this.request<RankSuccessResponse>('POST', `/api/ranks/groups/${encodeURIComponent(name)}/permissions`, { permission, value }, options);
  }

  /** Retire une permission d'un grade. */
  async removeGroupPermission(name: string, permission: string, value: boolean = true, options?: RequestOptions): Promise<{ data?: RankSuccessResponse; error?: string; status?: number; code?: string }> {
    return this.request<RankSuccessResponse>('DELETE', `/api/ranks/groups/${encodeURIComponent(name)}/permissions?permission=${encodeURIComponent(permission)}&value=${value}`, undefined, options);
  }

  /**
   * Ajoute un node entier à un grade : clé telle quelle (espaces de fin d'un préfixe compris), valeur,
   * et ses contextes et sa fin s'il en a. Copie des grades d'un groupe à l'autre, seulement vers un
   * satellite dont la lecture a dit `detailed` : un satellite d'avant ignorerait contextes et fin.
   */
  async addGroupNode(name: string, node: GroupNodeDTO, options?: RequestOptions): Promise<{ data?: RankSuccessResponse; error?: string; status?: number; code?: string }> {
    const body: { permission: string; value: boolean; context?: NodeContextDTO[]; expiry?: number } = { permission: node.key, value: node.value };
    if (node.context && node.context.length > 0) body.context = node.context.map((c) => ({ key: c.key, value: c.value }));
    if (typeof node.expiry === 'number') body.expiry = node.expiry;
    return this.request<RankSuccessResponse>('POST', `/api/ranks/groups/${encodeURIComponent(name)}/permissions`, body, options);
  }

  /**
   * Retire un node entier d'un grade : LuckPerms ne retire que le node de mêmes contextes et de même
   * fin, d'où un `context=clé=valeur` par contexte. Même réserve que addGroupNode.
   */
  async removeGroupNode(name: string, node: GroupNodeDTO, options?: RequestOptions): Promise<{ data?: RankSuccessResponse; error?: string; status?: number; code?: string }> {
    const query = [`permission=${encodeURIComponent(node.key)}`, `value=${node.value}`];
    for (const c of node.context ?? []) query.push(`context=${encodeURIComponent(`${c.key}=${c.value}`)}`);
    if (typeof node.expiry === 'number') query.push(`expiry=${node.expiry}`);
    return this.request<RankSuccessResponse>('DELETE', `/api/ranks/groups/${encodeURIComponent(name)}/permissions?${query.join('&')}`, undefined, options);
  }

  // ==================== PACKS (configuration in-game) ====================

  /** Scanne le dossier de packs (ou le dossier de travail d'un groupe). */
  async getPacksScan(group?: string): Promise<{ data?: PacksScanResponse; error?: string }> {
    const qs = group ? `?group=${encodeURIComponent(group)}` : '';
    return this.request<PacksScanResponse>('GET', `/api/packs/scan${qs}`);
  }

  /** Liste les groupes de packs (git-backed) configurés. */
  async getPacksGroups(): Promise<{ data?: PacksGroupsResponse; error?: string }> {
    return this.request<PacksGroupsResponse>('GET', '/api/packs/groups');
  }

  /**
   * Clone/met à jour les repos BP+RP d'un groupe. Le travail non publié est sauvegardé puis gardé
   * par-dessus GitHub (fusion par clé des agrégats, conflits signalés dans `conflicts`).
   */
  async syncPacksGroup(id: string): Promise<{ data?: PacksSyncResult; error?: string }> {
    return this.request<PacksSyncResult>('POST', `/api/packs/groups/${encodeURIComponent(id)}/sync`);
  }

  /** Liste les branches distantes disponibles pour un groupe (+ branche courante). */
  async getPackGroupBranches(id: string): Promise<{ data?: PackGroupBranches; error?: string }> {
    return this.request<PackGroupBranches>('GET', `/api/packs/groups/${encodeURIComponent(id)}/branches`);
  }

  /** Change la branche de travail d'un groupe (persiste + resynchronise). */
  async switchPackGroupBranch(id: string, branch: string): Promise<{ data?: PacksSyncResult; error?: string }> {
    return this.request<PacksSyncResult>('POST', `/api/packs/groups/${encodeURIComponent(id)}/branch`, { branch });
  }

  /** Crée ou met à jour un groupe de packs. */
  async savePackGroup(input: PackGroupInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', '/api/packs/groups', input);
  }

  /** Supprime un groupe de packs. */
  async deletePackGroup(id: string): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('DELETE', `/api/packs/groups/${encodeURIComponent(id)}`);
  }

  // ===== Types custom (catégories) =====

  /** Liste les types définis pour un projet + kind (block/item/entity/craft). */
  async getPackTypes(project: string, kind: string): Promise<{ data?: { types: PackType[] }; error?: string }> {
    return this.request('GET', `/api/packs/types?project=${encodeURIComponent(project)}&kind=${encodeURIComponent(kind)}`);
  }

  /** Crée ou met à jour un type. */
  async savePackType(input: PackType): Promise<{ data?: PackTypeSaveResult; error?: string }> {
    return this.request('POST', '/api/packs/types', input);
  }

  /** Supprime un type (et retire ses assignations). */
  async deletePackType(id: string): Promise<{ data?: PackTypeSaveResult; error?: string }> {
    return this.request('DELETE', `/api/packs/types/${encodeURIComponent(id)}`);
  }

  /** Map identifiant -> typeId pour un projet + kind. */
  async getPackEntryTypes(project: string, kind: string): Promise<{ data?: Record<string, string>; error?: string }> {
    return this.request('GET', `/api/packs/entry-types?project=${encodeURIComponent(project)}&kind=${encodeURIComponent(kind)}`);
  }

  /** Assigne (ou retire si typeId vide) le type d'une entrée. */
  async setPackEntryType(project: string, kind: string, identifier: string, typeId: string): Promise<{ data?: PackTypeSaveResult; error?: string }> {
    return this.request('POST', '/api/packs/entry-type', { project, kind, identifier, typeId });
  }

  /** Liste les blocks (behavior + resource fusionnés) d'un groupe. */
  async getPackBlocks(groupId: string): Promise<{ data?: PackBlocksResponse; error?: string }> {
    return this.request<PackBlocksResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/blocks`);
  }

  /** Liste les items d'un groupe. */
  async getPackItems(groupId: string): Promise<{ data?: PackItemsResponse; error?: string }> {
    return this.request<PackItemsResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/items`);
  }

  /** Liste les entities d'un groupe. */
  async getPackEntities(groupId: string): Promise<{ data?: PackEntitiesResponse; error?: string }> {
    return this.request<PackEntitiesResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/entities`);
  }

  /** Récupère un fichier JSON de pack (geometry, etc.) parsé. */
  async getPackFileJson(groupId: string, path: string): Promise<{ data?: unknown; error?: string }> {
    return this.request<unknown>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/file?path=${encodeURIComponent(path)}`);
  }

  /** Enregistre le contenu d'un fichier de pack (édition). */
  async savePackFile(groupId: string, path: string, content: string): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/file`, { path, content });
  }

  /** Objectifs du behavior pack Jobs (gains d'XP) d'un groupe, en une requête. */
  async getJobObjectives(groupId: string): Promise<{ data?: { objectives: JobObjective[] }; error?: string }> {
    return this.request('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/job-objectives`);
  }

  /** Supprime un fichier de pack (block/item/entité/recette). */
  async deletePackFile(groupId: string, path: string): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('DELETE', `/api/packs/groups/${encodeURIComponent(groupId)}/file?path=${encodeURIComponent(path)}`);
  }

  /** Liste le contenu d'un dossier du groupe (navigateur de fichiers). */
  async getPackFileTree(groupId: string, path: string): Promise<{ data?: PackTreeResponse; error?: string }> {
    return this.request<PackTreeResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/tree?path=${encodeURIComponent(path)}`);
  }

  /** Fichiers modifiés localement (édités, non publiés) d'un groupe. */
  async getPackChanges(groupId: string): Promise<{ data?: { files: string[] }; error?: string }> {
    return this.request('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/changes`);
  }

  /** Fichiers du working tree modifiés mais pas encore publiés (git status), pour le déploiement. */
  async getPackModifiedFiles(groupId: string): Promise<{ data?: { files: string[] }; error?: string }> {
    return this.request('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/modified-files`);
  }

  /** Annule (rollback) des modifications non publiées : restaure depuis HEAD / supprime les nouveaux fichiers. */
  async revertPackFiles(groupId: string, files: string[]): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/revert`, { files });
  }

  /** Fichiers modifiés dans le panel ET sur GitHub : version du panel gardée, publication bloquée. */
  async getPackConflicts(groupId: string): Promise<{ data?: { conflicts: PackConflictDTO[] }; error?: string }> {
    return this.request('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/conflicts`);
  }

  /** Tranche des conflits en gardant la version du panel (elle écrasera celle de GitHub à la publication). */
  async resolvePackConflicts(groupId: string, files: string[]): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/conflicts/resolve`, { files });
  }

  // ===== Containers de prod (par type de serveur) =====

  async getContainers(): Promise<{ data?: { containers: PackContainer[] }; error?: string }> {
    return this.request('GET', '/api/packs/containers');
  }

  /**
   * Clone/met à jour un container sur main. Le travail non publié est sauvegardé puis gardé
   * par-dessus main (fusion JSON clé par clé), les conflits sont listés dans `conflicts`.
   */
  async syncContainer(id: string): Promise<{ data?: ContainerSyncResult; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/sync`);
  }

  /** Fichiers de container modifiés dans le panel ET sur main : version du panel gardée, publication bloquée. */
  async getContainerConflicts(id: string): Promise<{ data?: { conflicts: ContainerConflictDTO[] }; error?: string }> {
    return this.request('GET', `/api/packs/containers/${encodeURIComponent(id)}/conflicts`);
  }

  /** Tranche des conflits de container en gardant la version du panel (publiée telle quelle). */
  async resolveContainerConflicts(id: string, files: string[]): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/conflicts/resolve`, { files });
  }

  /**
   * Tranche des conflits de container en prenant la version de main : pour un JSON fusionné, seules
   * les clés en conflit reprennent la valeur de main ; sinon le fichier entier revient à main.
   */
  async takeMainContainerConflicts(id: string, files: string[]): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/conflicts/take-main`, { files });
  }

  /** Déclare (ou met à jour) un container de serveur : c'est ce qui rend un projet éditable
   * (apparence, chat, boutique, index des cosmetics vivent dans la config d'un container). */
  async saveContainer(input: PackContainer): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', '/api/packs/containers', input);
  }

  async deleteContainer(id: string): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('DELETE', `/api/packs/containers/${encodeURIComponent(id)}`);
  }

  /** Déclare un cosmetic du pack dans le NametagSettings d'un serveur (nom → index du pack).
   * Nécessaire avant qu'un grade puisse le porter : la config ne connaît que ce qu'on y écrit. */
  async declareContainerCosmetic(id: string, type: 'nametag' | 'ranktag', name: string, index: number): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/cosmetic`, { type, name, index });
  }

  /** Cherche des fichiers DANS le container (récursif, côté serveur) : suffixe de nom et/ou
   * contenu. Évite de deviner les chemins des configs, qui varient d'un mod à l'autre. */
  async searchContainerFiles(
    id: string,
    q: { name?: string; contains?: string; jsonKey?: string; jsonKind?: 'array' | 'object'; max?: number } = {},
  ): Promise<{ data?: { entries: PackFileNode[] }; error?: string }> {
    const params = new URLSearchParams();
    if (q.name) params.set('name', q.name);
    if (q.contains) params.set('contains', q.contains);
    // jsonKey/jsonKind : la clé doit exister à la RACINE du JSON, sinon un fichier qui mentionne
    // juste le mot ressort dans les résultats (un LeaderboardMod matchait « Loots »).
    if (q.jsonKey) params.set('jsonKey', q.jsonKey);
    if (q.jsonKind) params.set('jsonKind', q.jsonKind);
    params.set('max', String(q.max ?? 50));
    return this.request('GET', `/api/packs/containers/${encodeURIComponent(id)}/search?${params.toString()}`);
  }

  /**
   * Activation des modules du panel par projet, valeurs résolues (réglage, ancien réglage, défaut
   * du module) pour chaque projet connu. Endpoint a FAIBLE privilege : c'est la barre de
   * navigation de tout membre du staff qui en a besoin, pas seulement celle d'un admin.
   */
  async getProjectPages(): Promise<{ data?: Record<string, Record<string, boolean>>; error?: string }> {
    return this.request('GET', '/api/panel/project-pages');
  }

  /** Reservee aux admins. Seules les clés envoyées changent ; la réponse est la carte complète. */
  async saveProjectPages(project: string, pages: Record<string, boolean>): Promise<{ data?: Record<string, boolean>; error?: string }> {
    return this.request('POST', `/api/panel/project-pages/${encodeURIComponent(project)}`, pages);
  }

  /**
   * Drapeaux d'activation GLOBALE des fonctionnalités optionnelles. Même logique de privilège que
   * getProjectPages : la navigation de tout le staff en dépend, la lecture est donc ouverte.
   */
  async getPanelFeatures(): Promise<{ data?: { features: PanelFeatureDTO[] }; error?: string }> {
    return this.request('GET', '/api/panel/features');
  }

  /** Reservee aux admins. */
  async savePanelFeatures(flags: Record<string, boolean>): Promise<{ data?: { features: PanelFeatureDTO[] }; error?: string }> {
    return this.request('PUT', '/api/panel/features', flags);
  }

  /**
   * Config commune à tous les containers du projet : sections éditables lues dans le container de
   * référence (le premier, par id, qui porte le fichier) et écarts des autres containers. Les
   * containers et le chemin du fichier dans chacun sont résolus par le serveur.
   */
  async inspectContainerConfig(query: ConfigSectionsQuery): Promise<{ data?: ConfigInspection; error?: string }> {
    return this.request<ConfigInspection>('POST', '/api/packs/containers/config/inspect', query);
  }

  /**
   * Enregistre les sections éditées dans CHAQUE container du projet qui porte le fichier (fusion de
   * section, le reste de chaque fichier intact). Rien n'est publié.
   */
  async applyContainerConfig(input: ConfigSectionsApplyInput): Promise<{ data?: ContainerApplyResult; error?: string }> {
    return this.request<ContainerApplyResult>('POST', '/api/packs/containers/config/apply', input);
  }

  async getContainerTree(id: string, path: string): Promise<{ data?: { entries: PackFileNode[] }; error?: string }> {
    return this.request('GET', `/api/packs/containers/${encodeURIComponent(id)}/tree?path=${encodeURIComponent(path)}`);
  }

  async saveContainerFile(id: string, path: string, content: string): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/file`, { path, content });
  }

  async getContainerModifiedFiles(id: string): Promise<{ data?: { files: string[] }; error?: string }> {
    return this.request('GET', `/api/packs/containers/${encodeURIComponent(id)}/modified-files`);
  }

  async revertContainerFiles(id: string, files: string[]): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/revert`, { files });
  }

  async publishContainer(id: string, message?: string): Promise<{ data?: { success: boolean; message: string; version: string | null; conflicts?: string[] }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/publish`, { message });
  }

  async finalizeContainer(id: string, version: string): Promise<{ data?: { merged: boolean; waiting: boolean; message: string; prUrl: string | null }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/finalize`, { version });
  }

  // ===== Publications (et demandes d'édition de ceux qui n'ont pas le droit de publier) =====

  /**
   * « Envoyer » une cible (groupe de packs ou image) : publie tout de suite si l'appelant a le droit
   * de valider les demandes, sinon crée une demande en attente. Le serveur tranche, pas le navigateur.
   */
  async sendDeploy(input: {
    project: string;
    type: 'pack' | 'container';
    targetId: string;
    targetName: string;
    files: string[];
    message?: string;
  }): Promise<{ data?: SendResultDTO; error?: string }> {
    return this.request('POST', '/api/packs/requests', { ...input, publish: true });
  }

  /** Liste les demandes d'édition d'un projet. */
  async listDeployRequests(project: string): Promise<{ data?: { requests: DeployRequestDTO[] }; error?: string }> {
    return this.request('GET', `/api/packs/requests?project=${encodeURIComponent(project)}`);
  }

  /** Accepte une demande → lance la publication réelle. `needsFinalize` (container) => poll finalizeContainer. */
  async acceptDeployRequest(id: string, decidedBy?: string): Promise<{ data?: PublishOutcomeDTO; error?: string }> {
    return this.request('POST', `/api/packs/requests/${encodeURIComponent(id)}/accept`, { decidedBy });
  }

  /** Refuse une demande (les éditions sur disque sont conservées). */
  async refuseDeployRequest(id: string, decidedBy?: string): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/requests/${encodeURIComponent(id)}/refuse`, { decidedBy });
  }

  /** Écarts de réglages entre les images du projet actif (relevé gardé une minute côté serveur). */
  async getContainerDivergences(refresh = false): Promise<{ data?: DivergenceReportDTO; error?: string }> {
    return this.request('GET', `/api/packs/containers/divergences${refresh ? '?refresh=true' : ''}`);
  }

  /** Marque l'écart d'un fichier comme voulu (pour son état actuel), ou le refait apparaître. */
  async ackContainerDivergence(path: string, fingerprint: string, acknowledged: boolean): Promise<{ data?: DivergenceReportDTO; error?: string }> {
    return this.request('POST', '/api/packs/containers/divergences/ack', { path, fingerprint, acknowledged });
  }

  /** Diff git d'un fichier de pack (working tree vs HEAD). */
  async getPackDiff(groupId: string, path: string): Promise<{ data?: { diff: string }; error?: string }> {
    return this.request('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/diff?path=${encodeURIComponent(path)}`);
  }

  /** Diff git d'un fichier de container (working tree vs HEAD). */
  async getContainerDiff(id: string, path: string): Promise<{ data?: { diff: string }; error?: string }> {
    return this.request('GET', `/api/packs/containers/${encodeURIComponent(id)}/diff?path=${encodeURIComponent(path)}`);
  }

  // ===== Administration du panel (users & grades) =====

  /** Catalogue des permissions (projets × pages) pour l'éditeur de grade. */
  async getPanelCatalog(): Promise<{ data?: { projects: PanelCatalogItem[]; pages: PanelCatalogItem[] }; error?: string }> {
    return this.request('GET', '/api/panel/catalog');
  }

  async getPanelGrades(): Promise<{ data?: { grades: PanelGradeDTO[] }; error?: string }> {
    return this.request('GET', '/api/panel/grades');
  }

  /** Crée (id absent) ou met à jour un grade panel. */
  async savePanelGrade(input: {
    id?: string; name: string; color: string; admin: boolean; permissions: string[];
    manageAccess?: boolean; canAssignGrades?: boolean; assignableGradeIds?: string[];
  }): Promise<{ data?: PanelGradeDTO; error?: string }> {
    return this.request('POST', '/api/panel/grades', input);
  }

  async deletePanelGrade(id: string): Promise<{ data?: { success: boolean }; error?: string }> {
    return this.request('DELETE', `/api/panel/grades/${encodeURIComponent(id)}`);
  }

  /** Réordonne les grades (ids du HAUT vers le bas). */
  async reorderPanelGrades(ids: string[]): Promise<{ data?: { success: boolean }; error?: string }> {
    return this.request('PUT', '/api/panel/grades/order', { ids });
  }

  /** Grades in-game protégés (éditables admin uniquement ; inclut toujours "admin"). */
  async getProtectedGrades(): Promise<{ data?: { names: string[] }; error?: string }> {
    return this.request('GET', '/api/panel/protected-grades');
  }

  /** Définit la liste des grades in-game protégés (admin uniquement). */
  async setProtectedGrades(names: string[]): Promise<{ data?: { names: string[] }; error?: string }> {
    return this.request('PUT', '/api/panel/protected-grades', { names });
  }

  async getPanelUsers(): Promise<{ data?: { users: PanelUserDTO[] }; error?: string }> {
    return this.request('GET', '/api/panel/users');
  }

  // ==================== PROJETS (backends satellites) ====================

  /** Liste les projets déclarés et l'état de leur satellite. */
  async getPanelProjects(): Promise<{ data?: { projects: PanelProjectDTO[] }; error?: string }> {
    return this.request('GET', '/api/panel/projects');
  }

  /** Crée ou met à jour un projet. Admin uniquement côté backend. */
  async savePanelProject(input: PanelProjectInput): Promise<{ data?: PanelProjectDTO; error?: string }> {
    return this.request('POST', '/api/panel/projects', input);
  }

  /**
   * Interroge immédiatement /api/_meta du satellite et rafraîchit ses capacités. Projet à groupes :
   * sonde aussi chaque cible et relit le catalogue du CICD (`catalog`).
   */
  async probePanelProject(
    id: string
  ): Promise<{ data?: { ok: boolean; message: string; project: PanelProjectDTO; catalog?: { ok: boolean; message: string } }; error?: string }> {
    return this.request('PUT', `/api/panel/projects/${encodeURIComponent(id)}/probe`);
  }

  async deletePanelProject(id: string): Promise<{ data?: { success: boolean }; error?: string }> {
    return this.request('DELETE', `/api/panel/projects/${encodeURIComponent(id)}`);
  }

  // ---- Groupes de jeu (NationsGlory : Nova, Iris...) ----

  /**
   * Groupes de jeu par projet, pour le sélecteur (droit d'accès au panel). Un projet sans groupes
   * est absent. N'est appelé que pour un jeu à groupes : Paladium ne fait jamais cet appel.
   */
  async getProjectGroups(): Promise<{ data?: Record<string, ProjectGroupsEntryDTO>; error?: string; status?: number }> {
    return this.request('GET', '/api/panel/project-groups');
  }

  /** Racine, jeton CICD et cibles par groupe. Admin ; 503 si le CICD est injoignable. */
  async savePanelProjectGameGroups(
    id: string,
    input: PanelGameGroupsInput
  ): Promise<{ data?: PanelProjectDTO; error?: string; status?: number }> {
    return this.request('PUT', `/api/panel/projects/${encodeURIComponent(id)}/game-groups`, input);
  }

  /** Retire la cible d'un groupe : le sélecteur ne propose plus ce groupe. */
  async deletePanelProjectGameGroupTarget(
    id: string,
    key: string
  ): Promise<{ data?: PanelProjectDTO; error?: string; status?: number }> {
    return this.request('DELETE', `/api/panel/projects/${encodeURIComponent(id)}/game-groups/targets/${encodeURIComponent(key)}`);
  }

  /** Revient au satellite unique. 409 tant que le satellite racine annonce un groupe. */
  async deletePanelProjectGameGroups(id: string): Promise<{ data?: PanelProjectDTO; error?: string; status?: number }> {
    return this.request('DELETE', `/api/panel/projects/${encodeURIComponent(id)}/game-groups`);
  }

  /** Définit la LISTE des grades d'un user (liste vide = aucun accès). */
  async setPanelUserGrades(sub: string, gradeIds: string[]): Promise<{ data?: { success: boolean }; error?: string }> {
    return this.request('PUT', `/api/panel/users/${encodeURIComponent(sub)}/grades`, { gradeIds });
  }

  /**
   * Affectation d'un user aux groupes de jeu d'un projet : `groups` null = tous les groupes, liste =
   * ces groupes seulement (vide = aucun). Un gestionnaire non admin n'accorde que ses propres groupes.
   */
  async setPanelUserGameGroups(
    sub: string,
    project: string,
    groups: string[] | null
  ): Promise<{ data?: { success: boolean; project: string; groups: string[] | null }; error?: string; status?: number }> {
    return this.request('PUT', `/api/panel/users/${encodeURIComponent(sub)}/game-groups`, { project, groups });
  }

  /** Définit l'admin d'un user : global et/ou limité à certains projets (admin uniquement). */
  async setPanelUserAdmin(sub: string, globalAdmin: boolean, adminProjects: string[]): Promise<{ data?: { success: boolean }; error?: string }> {
    return this.request('PUT', `/api/panel/users/${encodeURIComponent(sub)}/admin`, { globalAdmin, adminProjects });
  }

  /** Lit le contenu texte d'un fichier de container. */
  async getContainerFileRaw(id: string, path: string): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      const url = `${this.mainBaseUrl}/api/packs/containers/${encodeURIComponent(id)}/file?path=${encodeURIComponent(path)}`;
      // X-Project : un container est propre à son projet (le lobby NG n'est pas celui de Paladium).
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}`, ...contextHeaders() } });
      if (!r.ok) return null;
      return await r.text();
    } catch {
      return null;
    }
  }

  /** Grades essentials (HomeLimit + ChatFormat) d'un container. */
  async getContainerEssentials(id: string): Promise<{ data?: EssentialsData; error?: string }> {
    return this.request('GET', `/api/packs/containers/${encodeURIComponent(id)}/essentials`);
  }

  /**
   * Un champ null n'est pas touché ; `clearHomeLimit` retire HomeLimit du grade (champ vidé), pour
   * qu'un enregistrement commun aligne aussi un container qui en porte un.
   */
  async saveContainerEssentialsGroup(
    id: string,
    grade: string,
    homeLimit: number | null,
    chatFormat: string | null,
    premiumChatFormat: string | null,
    opts: { clearHomeLimit?: boolean } = {},
  ): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/essentials/group`, {
      grade, homeLimit, clearHomeLimit: !!opts.clearHomeLimit, chatFormat, premiumChatFormat,
    });
  }

  /** Config premium (boutique) d'un container : Groups + noms nametag/ranktag. */
  async getContainerPremium(id: string): Promise<{ data?: PremiumData; error?: string }> {
    return this.request('GET', `/api/packs/containers/${encodeURIComponent(id)}/premium`);
  }

  /** Fusionne `values` dans le grade ; une valeur null retire le champ (champ vidé dans le panel). */
  async saveContainerPremiumGroup(id: string, grade: string, values: Record<string, unknown>): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/premium/group`, { grade, values });
  }

  /** Crée un cosmetic (ornement/nametag ou rank tag) : PNG + render controller + player entity + index config. */
  async createCosmetic(input: {
    project: string;
    type: string;
    name: string;
    fileName: string;
    fileData: string;
    groupId?: string;
    containerIds?: string[];
  }): Promise<{ data?: CosmeticCreateResult; error?: string }> {
    return this.request('POST', '/api/packs/cosmetic/create', input);
  }

  /** URL blob d'une texture cosmetic (nametag/ranktag), résolue via le render controller du pack.
   * `bust` (optionnel) force le contournement du cache après un remplacement de texture. */
  async getCosmeticBlobUrl(containerId: string, type: string, name: string, bust?: number, index?: number): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      let url = `${this.mainBaseUrl}/api/packs/cosmetic?container=${encodeURIComponent(containerId)}&type=${encodeURIComponent(type)}&name=${encodeURIComponent(name)}`;
      // Sans container, l'index vient du pack (position dans le render controller).
      if (index !== undefined && index >= 0) url += `&index=${index}`;
      if (bust) url += `&_=${bust}`;
      const r = await fetch(url, {
        // X-Project : le backend résout le pack cosmetic du projet actif (Pala vs NG)
        headers: { Authorization: `Bearer ${token}`, 'X-Project': useGameStore.getState().activeGameId },
        cache: bust ? 'reload' : 'default',
      });
      if (!r.ok) return null;
      return URL.createObjectURL(await r.blob());
    } catch {
      return null;
    }
  }

  /** Remplace la texture d'un cosmetic existant (PNG en data URL / base64).
   * `container` peut être vide (projet sans serveur déclaré) : on passe alors l'index du pack. */
  async replaceCosmeticTexture(container: string, type: string, name: string, fileData: string, index?: number): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', '/api/packs/cosmetic/texture', { container, type, name, fileData, index });
  }

  /** Supprime un cosmetic : le retire du registre des containers du projet + supprime son PNG. */
  async deleteCosmetic(container: string, project: string, type: string, name: string, index?: number): Promise<{ data?: { success: boolean; message: string; containers?: string[] }; error?: string }> {
    return this.request('POST', '/api/packs/cosmetic/delete', { container, project, type, name, index });
  }

  /** Réécrit les index NametagSettings de tous les serveurs du projet pour coller aux slots du pack.
   * À utiliser quand le pack a été réordonné : sinon un grade porte la texture d'un autre cosmetic. */
  async realignCosmetics(): Promise<{ data?: RealignResult; error?: string }> {
    return this.request('POST', '/api/packs/cosmetics/realign');
  }

  /** Cosmetics tels qu'ils existent DANS le resource pack du projet (render controller).
   * Seule source disponible quand aucun serveur du projet n'est déclaré dans le panel. */
  async listPackCosmetics(type: 'nametag' | 'ranktag'): Promise<{ data?: PackCosmeticList; error?: string }> {
    return this.request('GET', `/api/packs/cosmetics?type=${encodeURIComponent(type)}`);
  }

  /** Packs (cosmetics / glyphes) réellement utilisés par le projet actif, pour affichage. */
  async getCosmeticSources(page = 'F1'): Promise<{ data?: CosmeticSources; error?: string }> {
    return this.request('GET', `/api/packs/cosmetic/sources?page=${encodeURIComponent(page)}`);
  }

  /** URL blob de l'atlas de glyphs Bedrock (police) pour une page hex (ex. "F1"). */
  async getGlyphAtlasBlobUrl(page: string): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      const url = `${this.mainBaseUrl}/api/packs/glyph?page=${encodeURIComponent(page)}`;
      const r = await fetch(url, {
        // X-Project : l'atlas est cherché dans le pack glyphes du projet actif
        headers: { Authorization: `Bearer ${token}`, 'X-Project': useGameStore.getState().activeGameId },
      });
      if (!r.ok) return null;
      return URL.createObjectURL(await r.blob());
    } catch {
      return null;
    }
  }

  /** Commit + push de l'atlas de glyphes sur la branche de travail des packs (sans PR vers main). */
  async publishGlyphAtlas(page: string, message?: string): Promise<{ data?: PacksSyncResult; error?: string }> {
    return this.request('POST', '/api/packs/glyph/publish', { page, message });
  }

  /** Promotion de l'atlas vers main : PR develop→main + merge + tag de release. */
  async deliverGlyphAtlas(page: string, message?: string): Promise<{ data?: PacksSyncResult; error?: string }> {
    return this.request('POST', '/api/packs/glyph/deliver', { page, message });
  }

  /** Remplace l'atlas de glyphes glyph_<page>.png par un PNG (data URL / base64). */
  async replaceGlyphAtlas(
    page: string,
    fileData: string,
    fileName?: string,
  ): Promise<{ success: boolean; message: string; groupId?: string; relPath?: string }> {
    const token = authService.getToken();
    if (!token) return { success: false, message: 'Non authentifié' };
    try {
      const url = `${this.mainBaseUrl}/api/packs/glyph`;
      const r = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Project': useGameStore.getState().activeGameId,
        },
        body: JSON.stringify({ page, fileName: fileName ?? `glyph_${page}.png`, fileData }),
      });
      if (!r.ok) return { success: false, message: `Erreur ${r.status}` };
      return await r.json();
    } catch (e) {
      return { success: false, message: String(e) };
    }
  }

  /** URL blob authentifiée d'un fichier de container (aperçu image). */
  async getContainerFileBlobUrl(id: string, path: string): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      const url = `${this.mainBaseUrl}/api/packs/containers/${encodeURIComponent(id)}/file?path=${encodeURIComponent(path)}`;
      // X-Project : un container est propre à son projet (le lobby NG n'est pas celui de Paladium).
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}`, ...contextHeaders() } });
      if (!r.ok) return null;
      return URL.createObjectURL(await r.blob());
    } catch {
      return null;
    }
  }

  /** Lit le contenu brut (texte) d'un fichier de pack (pour .lang/.molang/.material…). */
  async getPackFileRaw(groupId: string, path: string): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      const url = `${this.mainBaseUrl}/api/packs/groups/${encodeURIComponent(groupId)}/file?path=${encodeURIComponent(path)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return null;
      return await r.text();
    } catch {
      return null;
    }
  }

  /** Publie (commit + push) les modifications d'un groupe. `files` = sous-ensemble à publier (sinon tout). */
  async publishPackGroup(groupId: string, message?: string, files?: string[]): Promise<{ data?: PacksSyncResult; error?: string }> {
    return this.request<PacksSyncResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/publish`, { message, files });
  }

  /** Liste des géométries existantes (pour la liste déroulante). */
  async getPackGeometries(groupId: string): Promise<{ data?: PackGeometriesResponse; error?: string }> {
    return this.request<PackGeometriesResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/geometries`);
  }

  /** Liste des textures terrain existantes (pour la liste déroulante). */
  async getPackTextures(groupId: string): Promise<{ data?: PackTexturesResponse; error?: string }> {
    return this.request<PackTexturesResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/textures`);
  }

  /** Liste des textures d'items existantes (atlas item_texture.json). */
  async getPackItemTextures(groupId: string): Promise<{ data?: PackTexturesResponse; error?: string }> {
    return this.request<PackTexturesResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/item-textures`);
  }

  /** Crée/écrit l'attachable d'un item (armure équipée ou item tenu en 3D). */
  async savePackAttachable(groupId: string, input: PackAttachableSaveInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/attachable/save`, input);
  }

  /** Récupère les traductions en_US/fr_FR d'une clé lang. */
  async getPackLang(groupId: string, key: string): Promise<{ data?: PackLangResponse; error?: string }> {
    return this.request<PackLangResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/lang?key=${encodeURIComponent(key)}`);
  }

  /** Enregistre un block via formulaire. */
  async saveBlockForm(groupId: string, input: PackBlockSaveInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/block/save`, input);
  }

  /** Enregistre un item via formulaire. */
  async saveItemForm(groupId: string, input: PackItemSaveInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/item/save`, input);
  }

  async saveEntityForm(groupId: string, input: PackEntitySaveInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/entity/save`, input);
  }

  /** Importe (upload) une géométrie. */
  async importPackGeometry(groupId: string, content: string): Promise<{ data?: PackImportResult; error?: string }> {
    return this.request<PackImportResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/geometry/import`, { content });
  }

  /** Importe (upload) une texture + met à jour l'atlas. */
  async importPackTexture(groupId: string, input: PackTextureImportInput): Promise<{ data?: PackImportResult; error?: string }> {
    return this.request<PackImportResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/texture/import`, input);
  }

  /** Charge un audio jouable en objectURL (transcode .fsb -> .wav côté serveur). Null si non transcodable. */
  async getPackSoundPreviewBlobUrl(groupId: string, path: string): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      const url = `${this.mainBaseUrl}/api/packs/groups/${encodeURIComponent(groupId)}/sound-preview?path=${encodeURIComponent(path)}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }

  /** Liste les sons définis (sound_definitions.json) du groupe. */
  async getPackSounds(groupId: string): Promise<{ data?: PackSoundsResponse; error?: string }> {
    return this.request<PackSoundsResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/sounds`);
  }

  /** Importe (upload) un son .ogg + l'enregistre dans sound_definitions.json. */
  async importPackSound(groupId: string, input: { name: string; fileData: string }): Promise<{ data?: PackImportResult; error?: string }> {
    return this.request<PackImportResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/sound/import`, input);
  }

  /** Crée une entrée minimale (block/item/entity) dans un groupe (section). */
  async createPackEntry(groupId: string, kind: string, identifier: string): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/create`, { kind, identifier });
  }

  /** Clés de box du groupe (items portant premium:lootbox_key ou premium:box_key) et la box que chacune ouvre. */
  async getBoxKeys(groupId: string): Promise<{ data?: BoxKeysResponse; error?: string }> {
    return this.request<BoxKeysResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/box-keys`);
  }

  /** Crée la clé d'une box dans le groupe (item, icône, noms, attachable). Rien n'est publié. */
  async createBoxKey(groupId: string, input: BoxKeyInput): Promise<{ data?: BoxKeyResult; error?: string }> {
    return this.request<BoxKeyResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/box-key`, input);
  }

  /** Liste toutes les recettes d'un groupe. */
  async getPackRecipes(groupId: string): Promise<{ data?: PackRecipesResponse; error?: string }> {
    return this.request<PackRecipesResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/recipes`);
  }

  /** Récupère la recette (grille 3×3) produisant un identifiant. */
  async getPackRecipe(groupId: string, identifier: string): Promise<{ data?: PackRecipe; error?: string }> {
    return this.request<PackRecipe>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/recipe?identifier=${encodeURIComponent(identifier)}`);
  }

  /** Enregistre une recette de craft (shaped). */
  async savePackRecipe(groupId: string, input: PackRecipeSaveInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/recipe/save`, input);
  }

  /** Récupère l'attachable d'un item (armure : texture + géométrie). */
  async getPackAttachable(groupId: string, identifier: string): Promise<{ data?: PackAttachable; error?: string }> {
    return this.request<PackAttachable>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/attachable?identifier=${encodeURIComponent(identifier)}`);
  }

  /** Gros blocs de déco : bornes, verdict (fits/overflow/too_big/limit) et empreinte d'une géométrie du groupe. */
  async analyzePackGeometry(groupId: string, geometryId: string, threshold?: number): Promise<{ data?: PackGeometryAnalysis; error?: string }> {
    const th = threshold === undefined ? '' : `&threshold=${encodeURIComponent(String(threshold))}`;
    return this.request<PackGeometryAnalysis>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/geometry/analyze?geometryId=${encodeURIComponent(geometryId)}${th}`);
  }

  /** Même analyse pour une géométrie en attente (texte du .geo.json pas encore intégré). */
  async analyzePackGeometryText(groupId: string, content: string, geometryId?: string, threshold?: number): Promise<{ data?: PackGeometryAnalysis; error?: string }> {
    return this.request<PackGeometryAnalysis>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/geometry/analyze`, { content, geometryId, threshold });
  }

  /** Conversion groupée des blocs trop gros en objets (à blanc par défaut : dryRun absent = vrai). */
  async convertOversizedBlocks(groupId: string, input: PackOversizedBatchInput): Promise<{ data?: PackOversizedBatchReport; error?: string }> {
    return this.request<PackOversizedBatchReport>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/oversized/convert`, input);
  }

  /** Résout une géométrie (1.12 ou héritage 1.8) en une entrée plate {description, bones} pour le viewer. */
  async getPackGeometry(groupId: string, geometryId: string): Promise<{ data?: { description: any; bones: any[] }; error?: string }> {
    return this.request('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/geometry?geometryId=${encodeURIComponent(geometryId)}`);
  }

  /** Récupère les drops d'un block (loot table parsée). */
  async getPackDrop(groupId: string, identifier: string): Promise<{ data?: PackDrop; error?: string }> {
    return this.request<PackDrop>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/drop?identifier=${encodeURIComponent(identifier)}`);
  }

  /** Enregistre les drops d'un block. */
  async savePackDrop(groupId: string, input: PackDropSaveInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/drop/save`, input);
  }

  /**
   * Charge un fichier binaire de pack (texture PNG) en objectURL authentifié.
   * Les balises <img> et three.js ne peuvent pas envoyer le header Authorization,
   * on récupère donc le blob via fetch authentifié puis on crée un objectURL.
   * ⚠️ L'appelant DOIT révoquer l'URL (URL.revokeObjectURL) quand il a fini.
   */
  async getPackFileBlobUrl(groupId: string, path: string): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      const url = `${this.mainBaseUrl}/api/packs/groups/${encodeURIComponent(groupId)}/file?path=${encodeURIComponent(path)}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error('pack file blob failed:', err);
      return null;
    }
  }

  // ==================== STAFF ====================

  /** Liste de tous les staff connus (en ligne ou non). */
  async getStaffMembers(): Promise<{ data?: StaffListResponse; error?: string }> {
    return this.request<StaffListResponse>('GET', '/api/staff');
  }

  /** Temps de connexion journalier (minutes) d'un staff sur les N derniers jours. */
  async getStaffConnections(uuid: string, days: number = 7): Promise<{ data?: StaffConnectionsResponse; error?: string }> {
    return this.request<StaffConnectionsResponse>('GET', `/api/staff/${encodeURIComponent(uuid)}/connections?days=${days}`);
  }
}

// Online Players types
export interface OnlineSummaryResponse {
  totalPlayers: number;
  totalSlots: number;
  proxyCount: number;
  playersByServer: Record<string, number>;
}

export interface OnlinePlayerDTO {
  uuid: string;
  name: string;
  proxyName: string;
  serverName: string;
  connectedSince: number;
  isStaff: boolean;
  flags: Record<string, string>;
  /** Pseudo Recube du joueur ; absent sans pseudo ou sans registre Recube sur le satellite. */
  displayName?: string | null;
}

export interface OnlinePlayersListResponse {
  players: OnlinePlayerDTO[];
  total: number;
}

export interface OnlineCheckResponse {
  online: boolean;
  player: OnlinePlayerDTO | null;
}

export interface ProxyDTO {
  proxyName: string;
  playerCount: number;
  slots: number;
  alive: boolean;
  lastKeepAlive: number;
}

export interface ProxiesListResponse {
  proxies: ProxyDTO[];
  total: number;
}

export interface ServerStatsDTO {
  serverName: string;
  playerCount: number;
}

export interface ServerStatsListResponse {
  servers: ServerStatsDTO[];
  totalPlayers: number;
}

// Player Stats types
export interface StatsDataPoint {
  timestamp: number;
  totalPlayersAvg: number;
  totalPlayersMin: number;
  totalPlayersMax: number;
  staffCountAvg: number;
  staffCountMin: number;
  staffCountMax: number;
}

export interface ServerStatsDataPoint {
  timestamp: number;
  avg: number;
  min: number;
  max: number;
}

export interface PlayerStatsResponse {
  globalStats: StatsDataPoint[];
  serverStats: Record<string, ServerStatsDataPoint[]>;
}

export interface ActivityStatsDTO {
  newPlayers: number;
  uniquePlayers: number;
  totalMinutes: number;
}

export interface PlayerActivityResponse {
  startDate: string;
  endDate: string;
  current: ActivityStatsDTO;
  previous: ActivityStatsDTO;
}

export interface DaySeriesDTO {
  date: string;
  newPlayers: number;
  uniquePlayers: number;
  minutes: number;
}

export interface PlayerActivitySeriesResponse {
  startDate: string;
  endDate: string;
  days: DaySeriesDTO[];
}

// Server Logs History types
export interface ServerLogEntryDTO {
  timestamp: string;
  serverIdentifier: string;
  serverName: string;
  message: string;
  level: string;
}

export interface ServerLogsHistoryResponse {
  logs: ServerLogEntryDTO[];
  total: number;
  from: number;
  size: number;
}

export interface ServerLogsDatesResponse {
  dates: string[];
}

export interface LogsCollectorConnectionStatus {
  serverName: string;
  connected: boolean;
  logsCollected: number;
  lastLogTime: number;
}

export interface LogsCollectorStatusResponse {
  connections: Record<string, LogsCollectorConnectionStatus>;
  totalConnections: number;
  connectedCount: number;
}

// Transaction Stats types
export interface DailyTransactionStatsDTO {
  date: string;
  timestamp: number;
  reasons: Record<string, number>; // Key = exact reason string
  total: number;
}

export interface TransactionStatsResponse {
  days: DailyTransactionStatsDTO[];
  startDate: string;
  endDate: string;
  type: string | null;
}

/** Un jour des mouvements d'argent. Les montants par catégorie sont rangés par clé de catégorie. */
export interface VaultFlowDayDTO {
  date: string;
  timestamp: number;
  created: number;
  destroyed: number;
  /** Ce que des joueurs ont reçu d'autres joueurs, du marché ou d'une banque. */
  transferred: number;
  /** Crédits et débits des transactions qui ne disent pas ce qu'elles sont. */
  unknownIn: number;
  unknownOut: number;
  createdByCategory: Record<string, number>;
  destroyedByCategory: Record<string, number>;
  transferredByCategory: Record<string, number>;
}

export interface VaultFlowTotalsDTO {
  created: number;
  destroyed: number;
  /** created - destroyed */
  net: number;
  transferred: number;
  unknownIn: number;
  unknownOut: number;
}

export interface VaultFlowCategoryDTO {
  /** Clé de la catégorie ; null pour les transactions sans catégorie. */
  category: string | null;
  /** creation, destruction, transfer ou unknown. */
  flow: string;
  /** Ce que la catégorie pèse dans son sens : créé, détruit, reçu d'un autre joueur. */
  amount: number;
  /** Ses crédits et ses débits bruts : un dépôt en banque ne fait que sortir des porte-monnaie. */
  in: number;
  out: number;
  count: number;
}

export interface VaultFlowStatsResponse {
  startDate: string;
  endDate: string;
  totals: VaultFlowTotalsDTO;
  days: VaultFlowDayDTO[];
  categories: VaultFlowCategoryDTO[];
}

/** Une ligne du classement de l'argent. */
export interface MoneyLeaderboardRowDTO {
  /** 1 + comptes strictement plus riches : deux soldes égaux ont le même rang. */
  rank: number;
  uuid: string;
  /** Gamertag ; absent s'il n'est connu de personne (joueurs en ligne, roster, LuckPerms). */
  name?: string;
  /** Pseudo Recube ; absent sans pseudo. */
  displayName?: string;
  online: boolean;
  serverName?: string;
  money: number;
  /** Part de la monnaie en circulation, en pour cent. */
  share: number;
}

export interface MoneyLeaderboardResponse {
  /** Heure de la lecture (millisecondes). */
  now: number;
  /** Comptes d'argent du projet. */
  totalPlayers: number;
  /** Monnaie en circulation : somme des soldes positifs. */
  totalMoney: number;
  page: number;
  size: number;
  totalPages: number;
  /** La recherche telle que le satellite l'a cherchée ; absente sans recherche. */
  query?: string;
  players: MoneyLeaderboardRowDTO[];
  /** Des joueurs restent sans pseudo. */
  namesPartial: boolean;
}

export interface ReasonSummaryDTO {
  reason: string;
  totalAmount: number;
  giveAmount: number;
  clearAmount: number;
  transactionCount: number;
}

export interface ReasonsSummaryResponse {
  reasons: ReasonSummaryDTO[];
  startDate: string;
  endDate: string;
}

// Rank Management types
export interface LuckPermsGroupDTO {
  name: string;
  displayName: string;
  /** Poids LuckPerms (node weight.N) ; null : aucun ; absent : satellite d'avant BE-Panel-Games 1.0.9. */
  weight?: number | null;
}

export interface GroupsResponse {
  groups: LuckPermsGroupDTO[];
  protectedGrades?: string[]; // grades in-game éditables par admin uniquement (inclut toujours "admin")
}

export interface PlayerGroupsResponse {
  uuid: string;
  /** Grades attribués directement (nodes group.*). */
  groups: string[];
  primaryGroup: string;
  /** Grades hérités seulement, hors grades directs ; absent : satellite d'avant BE-Panel-Games 1.0.10. */
  inheritedGroups?: string[];
}

export interface RankSuccessResponse {
  message: string;
  groupName: string;
}

/** Un joueur et un de ses grades (GET /api/stats/grades). Fins en secondes epoch. */
export interface ActiveGradeRowDTO {
  uuid: string;
  /** Gamertag (en ligne, roster, sinon pseudo vu par LuckPerms) ; absent s'il est inconnu. */
  name?: string;
  /** Pseudo Recube ; absent sans pseudo ou sans registre Recube. */
  displayName?: string;
  online: boolean;
  serverName?: string;
  grade: string;
  /** Fin écrite dans LuckPerms ; absente pour un grade à vie. */
  expiry?: number;
  /** Fin au-delà de l'an 3000 : écrite en millisecondes par MailBoxMod. */
  aberrant: boolean;
  /** Vraie fin (valeur / 1000) d'une fin aberrante ; absente sinon, ou si illisible. */
  realExpiry?: number;
  /** Contextes du node (« server=faction-1 ») ; absents pour un grade global. */
  context?: string[];
}

export interface ActiveGradeCountDTO {
  grade: string;
  players: number;
  temporary: number;
  permanent: number;
  aberrant: number;
  /** Fins aberrantes dont la vraie fin est déjà passée. */
  aberrantPassed: number;
}

export interface ActiveGradesResponse {
  /** Heure du relevé (secondes epoch) : le temps restant se compte depuis elle. */
  now: number;
  aberrantAfter: number;
  grades: string[];
  counts: ActiveGradeCountDTO[];
  players: ActiveGradeRowDTO[];
  /** Grades que LuckPerms n'a pas donnés. */
  failures: string[];
  /** Des joueurs restent sans pseudo (LuckPerms trop lent pour tous les nommer). */
  namesPartial: boolean;
}

export type GradeRepairAction = 'CORRIGER' | 'RETIRER' | 'IGNORER';

/** Un node de grade à fin aberrante, et ce que la correction en fait. */
export interface GradeRepairRowDTO {
  uuid: string;
  name?: string;
  displayName?: string;
  online: boolean;
  grade: string;
  context?: string[];
  /** Fin écrite (ancienne valeur, gardée au journal). */
  storedExpiry: number;
  /** Vraie fin (valeur / 1000) ; absente si illisible. */
  realExpiry?: number;
  action: GradeRepairAction;
  /** Après la correction : corrige, retire, echec_retrait, retire_sans_remise, corrige_non_verifie, retire_non_verifie. */
  status?: string;
}

export interface GradeRepairPreview {
  now: number;
  aberrantAfter: number;
  /** À renvoyer tel quel pour confirmer : seule cette liste est appliquée. */
  fingerprint: string;
  /** Base « satellite » déclarée : sans elle, pas de journal, donc pas de correction. */
  journalAvailable: boolean;
  running: boolean;
  toFix: number;
  toRemove: number;
  ignored: number;
  entries: GradeRepairRowDTO[];
  scannedGrades: string[];
  failures: string[];
}

export interface GradeRepairResult {
  runId: string;
  fingerprint: string;
  fixed: number;
  removed: number;
  failed: number;
  entries: GradeRepairRowDTO[];
}

/** Un joueur de l'export Tebex et ses grades dus, envoyés au satellite (aucune donnée personnelle). */
export interface TebexGrantPlayerDTO {
  /** Colonne Uuid de Tebex : le XUID Xbox. Le satellite en dérive l'uuid LuckPerms comme le moteur. */
  xuid: string;
  username: string;
  /** `needed` : grade à donner ; sinon grade en cours dominé, seulement daté s'il est déjà là. */
  grants: Array<{ grade: string; end: number; needed: boolean; purchases: Array<{ id: string; date: number; product: string }> }>;
  explained: string[];
}

/** Une ligne du rapprochement : un grade dû, son état dans LuckPerms et ce que la pose en fera. */
export interface TebexGrantLineDTO {
  uuid: string;
  xuid: string;
  username: string;
  lpName?: string;
  displayName?: string;
  renamed: boolean;
  online: boolean;
  grade: string;
  /** Grade à donner ; non : grade en cours dominé par un plus haut, seulement daté s'il est déjà là. */
  needed: boolean;
  expectedEnd: number;
  purchases: number;
  category: 'OK' | 'COUVERT' | 'ABSENT' | 'DATE_FAUSSE' | 'A_VIE' | 'MAUVAIS_GRADE' | 'FIN_TROP_TOT';
  action: 'AUCUNE' | 'POSER' | 'REMPLACER';
  currentGrade?: string;
  current?: number;
  currentPermanent: boolean;
  newEnd?: number;
  /** uuid_different : le pseudo Tebex est connu de LuckPerms sous un autre uuid (pas de pose) ; inconnu : jamais vu par LuckPerms. */
  warning?: 'uuid_different' | 'inconnu';
  status?: string;
}

/** Grade vendu que LuckPerms donne sans qu'aucun achat de l'export ne l'explique (signalé, jamais retiré). */
export interface TebexGrantExtraDTO {
  uuid: string;
  name?: string;
  displayName?: string;
  grade: string;
  expiry?: number;
  permanent: boolean;
  inExport: boolean;
}

export interface TebexGrantPreview {
  now: number;
  fingerprint: string;
  journalAvailable: boolean;
  running: boolean;
  replaceLifetime: boolean;
  players: number;
  counts: Record<string, number>;
  toPose: number;
  toReplace: number;
  renamed: number;
  uuidMismatch: number;
  unknown: number;
  lines: TebexGrantLineDTO[];
  extras: TebexGrantExtraDTO[];
  extraCount: number;
  extraPlayers: number;
  /** Grades à vie qu'un achat explique mais dont tous les achats sont finis (signalés, jamais retirés ici). */
  expiredLifetime: TebexGrantExtraDTO[];
  failures: string[];
}

/** Pose lancée : elle se poursuit en tâche de fond, son avancement se lit dans le journal (runId). */
export interface TebexGrantResult {
  runId: string;
  fingerprint: string;
  total: number;
  status: string;
}

/** Une ligne d'une pose Tebex, telle que le journal la garde (ancien node compris). */
export interface TebexRunEntryDTO {
  uuid: string;
  xuid: string;
  name?: string;
  grade: string;
  category: string;
  action: string;
  oldKey?: string | null;
  oldExpiry?: number | null;
  oldPermanent?: boolean;
  newExpiry?: number | null;
  status: string;
}

/** Une correction passée, telle que le journal du satellite la garde. */
export interface GradeRepairRunDTO {
  _id: string;
  /** « tebex » : pose des grades dus par les achats ; absent : correction des dates fausses. */
  kind?: string;
  posed?: number;
  /** Pose Tebex : lignes à traiter et déjà traitées (elle tourne en tâche de fond). */
  total?: number;
  done?: number;
  startedAt: number;
  finishedAt?: number;
  by: string;
  status: string;
  fixed?: number;
  removed?: number;
  failed?: number;
  entries: Array<GradeRepairRowDTO & { key: string; doneAt?: number }>;
}

export interface GradeRepairRunsResponse {
  journalAvailable: boolean;
  runs: GradeRepairRunDTO[];
}

/** Membre direct d'un grade (GET /api/ranks/groups/{name}/members). */
export interface GroupMemberDTO {
  uuid: string;
  /** Gamertag : joueur en ligne, sinon pseudo vu par LuckPerms ; absent s'il est inconnu. */
  name?: string;
  /** Pseudo Recube ; absent sans pseudo ou sans registre Recube. */
  displayName?: string;
  online: boolean;
  serverName?: string;
  /** Fin d'un grade temporaire (secondes epoch) ; absent s'il est permanent. */
  expiry?: number;
}

export interface GroupMembersResponse {
  group: string;
  members: GroupMemberDTO[];
  /** Membres directs dans LuckPerms ; `truncated` : la liste n'en montre qu'une partie. */
  total: number;
  truncated: boolean;
}

/** Joueur trouvé par GET /api/ranks/lookup. */
export interface RankPlayerLookupDTO {
  uuid: string;
  name?: string;
  displayName?: string;
  online: boolean;
  /** Origine de l'uuid : uuid, recube, online, profile-api, luckperms. */
  source: string;
}

/** Contexte d'un node LuckPerms : une paire clé et valeur (server=nova-1, world=nether). */
export interface NodeContextDTO {
  key: string;
  value: string;
}

export interface GroupNodeDTO {
  key: string;
  value: boolean;
  /** Contextes du node ; absent ou vide : node global. */
  context?: NodeContextDTO[];
  /** Fin d'un node temporaire (secondes epoch) ; absente : node permanent. */
  expiry?: number | null;
}

export interface GroupPermissionsResponse {
  permissions: GroupNodeDTO[];
  /**
   * Les nodes portent leurs contextes et leur fin. Absent : satellite qui ne les lit pas, un node
   * limité à un serveur y passe pour global (la copie des grades d'un groupe à l'autre le refuse).
   */
  detailed?: boolean;
}

// Staff types
export interface StaffMemberDTO {
  uuid: string;
  name: string;
  rank: string;          // PRIMARY_GROUP_ID (ex: "modo", "helper"...)
  isPremium: boolean;
  online: boolean;
  serverName: string | null;
  connectedSince: number; // timestamp ms (0 si hors-ligne)
  lastSeen: number;       // timestamp ms de la dernière présence
  /** Pseudo Recube du joueur ; absent sans pseudo ou sans registre Recube sur le satellite. */
  displayName?: string | null;
}

export interface StaffListResponse {
  staff: StaffMemberDTO[];
}

export interface ConnectionDayDTO {
  date: string;   // yyyy-MM-dd
  minutes: number;
}

export interface StaffConnectionsResponse {
  days: ConnectionDayDTO[];
  totalMinutes: number;
}

// Packs (configuration in-game)
export interface PackSummaryDTO {
  name: string;
  description: string;
  kind: string; // "behavior" | "resource" | "unknown"
  uuid: string;
  version: string;
  formatVersion: string;
  path: string;
}

export interface PacksScanResponse {
  packsRoot: string;
  exists: boolean;
  packs: PackSummaryDTO[];
}

export interface PackGroupDTO {
  id: string;
  name: string;
  type: string;
  color: string;
  behaviorRepo: string;
  resourceRepo: string;
  branch: string;
  behaviorBranch?: string;
  resourceBranch?: string;
  resourceReadonly?: boolean;
  /** Projets auxquels le groupe appartient (paladium / nationsglory / recube). */
  projects?: string[];
  /** Ce resource pack porte les cosmetics (ornements / rank tags) du projet. */
  cosmeticsSource?: boolean;
  /** Ce pack porte l'atlas de glyphes (glyph_<page>.png) du projet. */
  glyphSource?: boolean;
  /** Gros blocs de déco (objets PropsiMod) : analyse, conversion et action groupée. Faux par défaut. */
  oversizedBlocks?: boolean;
}

export interface PackType {
  id?: string;
  project: string;
  kind: string;
  name: string;
  color: string;
}

export interface PackTypeSaveResult {
  success: boolean;
  message: string;
  id: string | null;
}

export interface PackGroupInput {
  id: string;
  name: string;
  type: string;
  color: string;
  projects: string[];
  behaviorRepo: string;
  resourceRepo: string;
  branch: string;
  behaviorBranch?: string;
  resourceBranch?: string;
  resourceReadonly: boolean;
  cosmeticsSource: boolean;
  glyphSource: boolean;
  oversizedBlocks: boolean;
}

/** Pack réellement utilisé par le projet actif pour un rôle (cosmetics / glyphes). */
export interface PackRoleSource {
  role: string;
  groupId: string | null;
  groupName: string | null;
  resourceRepo: string | null;
  branch: string | null;
  /** true = pack marqué explicitement dans la config des groupes ; false = auto-détecté. */
  explicit: boolean;
  readOnly: boolean;
  found: boolean;
}

export interface CosmeticSources {
  project: string;
  cosmetics: PackRoleSource;
  glyph: PackRoleSource;
}

/** Un cosmetic tel qu'il existe dans le pack : sa position dans le render controller = son index. */
export interface PackCosmetic {
  index: number;
  shortName: string;
  /** Nom référencé par les grades (nom court sans le suffixe _tag / _rank_tag). */
  name: string;
  /** Nom de fichier de la texture, si la table textures de player.entity.json le donne. */
  file: string | null;
}

export interface PackCosmeticList {
  found: boolean;
  groupId: string | null;
  controller: string;
  items: PackCosmetic[];
}

export interface PacksGroupsResponse {
  groups: PackGroupDTO[];
}

export interface PacksSyncResult {
  success: boolean;
  messages: string[];
  /** Chemins "behavior/…" / "resource/…" modifiés dans le panel ET sur GitHub (à trancher). */
  conflicts?: string[];
}

/** Conflit en attente : version du panel gardée, sauvegarde sur le serveur. */
export interface PackConflictDTO {
  path: string;
  at: string;
  backup: string;
}

export interface PackGroupBranches {
  branches: string[];
  current: string;
}

/** Un objectif de métier du behavior pack Jobs : c'est lui qui définit le gain d'XP en jeu. */
export interface JobObjective {
  /** Chemin dans le groupe, ex. behavior/objectives/break/amethyst_ore.json */
  path: string;
  /** break | kill | craft | smelt */
  kind: string;
  job: string | null;
  target: string | null;
  icon: string | null;
  experience: number | null;
  levelNeeded: number | null;
  /** Texte d'origine du fichier : sert à le réécrire sans perdre les clés non gérées. */
  content: string;
}

export interface PackFileNode {
  name: string;
  path: string;
  dir: boolean;
  size: number;
}

export interface PackContainer {
  id: string;
  name: string;
  type: string;
  repo: string;
  project?: string;
  modsPath?: string;
}

/** Synchronisation d'un container : travail local gardé par-dessus main. */
export interface ContainerSyncResult {
  success: boolean;
  message: string;
  /** Ce que l'intégration de main a fait (fichiers gardés, fusionnés, sauvegarde, conflits). */
  details?: string[];
  /** Fichiers modifiés dans le panel ET sur main, à trancher avant publication. */
  conflicts?: string[];
}

/** Conflit de container en attente : version du panel dans le dossier, sauvegarde sur le serveur. */
export interface ContainerConflictDTO {
  path: string;
  at: string;
  backup: string;
  /** Clés modifiées des deux côtés (JSON fusionné), vide pour un conflit de fichier entier. */
  keys: string[];
  /** Raison d'un conflit de fichier entier. */
  note: string;
}

export interface PanelCatalogItem {
  key: string;
  label: string;
  group?: string; // famille de regroupement (ex. "Modération", "Configuration")
}

export interface PanelGradeDTO {
  id: string;
  name: string;
  color: string;
  admin: boolean;
  permissions: string[]; // clés <projet>.<page>
  manageAccess?: boolean;        // peut ouvrir « Grades & accès »
  canAssignGrades?: boolean;     // peut attribuer des grades
  assignableGradeIds?: string[]; // grades attribuables (si canAssignGrades)
  rank?: number;                 // rang hiérarchique (plus élevé = plus haut ; admins toujours au-dessus)
}

export interface PanelUserDTO {
  sub: string;
  handle: string;
  email: string | null;
  avatar: string | null;
  gradeIds: string[];
  gradeNames: string[];
  admin: boolean; // admin EFFECTIF : bootstrap OU grade admin OU admin user-level
  globalAdmin?: boolean;      // admin global posé au niveau du user (onglet Admins)
  adminProjects?: string[];   // admin limité à ces projets
  bootstrapAdmin?: boolean;   // admin d'office via l'env recube.admins (non éditable ici)
  gradeAdmin?: boolean;       // admin via un de ses grades (s'édite sur le grade)
  firstSeen: number;
  lastLogin: number;
  /**
   * Affectation enregistrée aux groupes de jeu, { projet : [clés] } : projet absent = tous ses
   * groupes, liste vide = aucun. Un admin (global ou du projet) a de toute façon tous les groupes.
   */
  gameGroups?: Record<string, string[]>;
}

/** Module du panel (catalogue serveur) et son drapeau d'activation GLOBALE. */
export interface PanelFeatureDTO {
  key: string;
  label: string;
  group: string; // espace : "Supervision" | "Configuration" | "Statistiques"
  /** Phrase courte affichée sous le libellé dans Paramètres généraux > Fonctionnalités. */
  description?: string | null;
  /** Module dont celui-ci est une section (ex. Pays des statistiques), absent sinon. */
  parent?: string | null;
  /** Projets où il est actif sans réglage, absent ou null = tous. */
  defaultProjects?: string[] | null;
  enabled: boolean;
}

/**
 * Auto-description remontée par le satellite d'un projet (son endpoint /api/_meta), sondée
 * chaque minute par le panel. Absente tant que le satellite n'a jamais répondu.
 */
export interface ProjectMetaDTO {
  project?: string;
  displayName?: string;
  version?: string;
  capabilities?: string[];
  modules?: Record<string, boolean>;
  datasources?: Record<string, string>; // nom logique -> "up" | "down"
  integrations?: Record<string, boolean>;
  collectors?: string[];
  lastSeen?: number;
  healthy?: boolean;
  error?: string;
  /** Groupe de jeu servi (`project.group` du satellite). Absent d'un satellite sans groupe. */
  group?: string;
  /** Nom logique -> nom de base (jamais l'URI). Seulement sur un satellite de groupe. */
  databases?: Record<string, string>;
  /**
   * Nom logique -> serveur de la base (« hôte:port », jamais l'URI ni ses identifiants). Satellite de
   * groupe 1.0.19 ou plus : absent d'un satellite plus ancien, le panel compare alors le nom seul.
   */
  databaseServers?: Record<string, string>;
  /** Groupes que ce satellite d'entrée relaie (`relay.groups`), sans leurs adresses. */
  relay?: string[];
  /** Cible sondée à travers le relais du satellite d'entrée. */
  relayed?: boolean;
  /** Avertissements de la sonde (groupe non annoncé, base partagée entre groupes...). */
  warnings?: string[];
}

/** Cible d'un groupe de jeu dans le registre du panel (vue publique : aucune clé). */
export interface PanelGameGroupTargetDTO {
  baseUrl: string | null;
  signingKeyId: string | null;
  hasSigningKey: boolean;
  enabled: boolean;
  timeoutMs: number;
  meta: ProjectMetaDTO | null;
}

/** Groupe du catalogue du CICD (`projects.groups`). */
export interface CatalogGameGroupDTO {
  key: string;
  label: string;
  color?: string;
}

/** Groupes de jeu d'un projet (NationsGlory seulement), vue publique : ni jeton CICD, ni clé. */
export interface PanelGameGroupsDTO {
  /** Groupe servi par le satellite racine (champs baseUrl, kid... du projet). */
  root: string | null;
  hasCicdToken: boolean;
  targets: Record<string, PanelGameGroupTargetDTO>;
  /** Dernier catalogue lu dans le CICD, absent s'il n'a jamais été lu. */
  catalog?: {
    fetchedAt?: number;
    groups: CatalogGameGroupDTO[];
    error?: string;
    errorAt?: number;
  };
}

/** Un projet et son backend satellite (collection PanelProjects). */
export interface PanelProjectDTO {
  id: string;
  name: string;
  color: string | null;
  baseUrl: string | null;
  signingKeyId: string | null;
  /** La clé n'est jamais renvoyée par l'API : on sait seulement si elle est renseignée. */
  hasSigningKey: boolean;
  enabled: boolean;
  timeoutMs: number;
  meta: ProjectMetaDTO | null;
  /** Absent pour un projet sans groupes de jeu (Paladium). */
  gameGroups?: PanelGameGroupsDTO;
  /**
   * Projet sans groupes relayé par un satellite d'entrée (Recube par Iris) : `baseUrl` est celle du
   * satellite d'entrée, qui le relaie sous cette clé (relay.groups.<clé>). Absent sinon.
   */
  relayGroup?: string;
}

/** Saisie d'une cible de groupe. `signingKey` omise ou vide = on garde la clé existante. */
export interface PanelGameGroupTargetInput {
  baseUrl: string;
  signingKeyId: string;
  signingKey?: string;
  enabled?: boolean;
  timeoutMs?: number;
}

/**
 * `PUT /api/panel/projects/{id}/game-groups`. `cicdToken` omis ou vide = jeton existant gardé ;
 * `targets` ajoute ou remplace les cibles citées, les autres restent.
 */
export interface PanelGameGroupsInput {
  root: string;
  cicdToken?: string;
  targets?: Record<string, PanelGameGroupTargetInput>;
}

/** Un groupe de jeu vu par le sélecteur (`GET /api/panel/project-groups`), sans URL ni kid. */
export interface ProjectGroupDTO {
  key: string;
  label: string;
  color?: string;
  /** Groupe servi par le satellite racine. */
  root: boolean;
  /** La passerelle accepte ce groupe (pour l'appelant). La santé n'y entre jamais. */
  available: boolean;
  /**
   * Motif quand `available` est faux (« Aucun satellite déclaré pour Iris. »), ou `not-member`
   * (NOT_MEMBER_REASON) : l'appelant n'est pas affecté à ce groupe, qui ne lui est jamais proposé.
   */
  reason?: string;
  /** Cible gardée au registre alors que son groupe a quitté le CICD (ni servie, ni affectable). */
  retired?: boolean;
  /** Dernière sonde : pour l'affichage seulement, un groupe injoignable reste choisissable. */
  healthy: boolean;
  healthError?: string;
  capabilities: string[];
  warnings?: string[];
}

/** Entrée d'un projet à groupes dans `GET /api/panel/project-groups`. */
export interface ProjectGroupsEntryDTO {
  root: string;
  /** « cicd » : catalogue du CICD lu ; « registry » : jamais lu, clés du registre. */
  source: 'cicd' | 'registry';
  /** Ordre du CICD ; la racine en tête si elle en a disparu, les groupes retirés en dernier. */
  groups: ProjectGroupDTO[];
}

/** Payload d'écriture. `signingKey` omis ou vide = on conserve la clé existante. */
export interface PanelProjectInput {
  id: string;
  name: string;
  color?: string;
  baseUrl: string;
  signingKeyId?: string;
  signingKey?: string;
  enabled?: boolean;
  timeoutMs?: number;
  /** Clé de relais (cf PanelProjectDTO.relayGroup) : omise = gardée, vide = retirée. */
  relayGroup?: string;
}

export interface DeployRequestDTO {
  id: string;
  project: string;
  type: 'pack' | 'container';
  targetId: string;
  targetName: string;
  files: string[];
  message: string;
  requestedBy: string;
  createdAt: number;
  /**
   * `publishing` : publication directe en cours ; `failed` : publication directe en échec, rien n'est
   * parti (les fichiers restent à envoyer).
   */
  status: 'pending' | 'publishing' | 'accepted' | 'refused' | 'failed';
  decidedBy: string | null;
  decidedAt: number | null;
  result: string | null;
  /** Publiée par son auteur sans validation (il avait le droit de publier). */
  direct?: boolean;
}

/** Compte rendu d'une publication (acceptation d'une demande ou publication directe). */
export interface PublishOutcomeDTO {
  success: boolean;
  message: string;
  /** Image : la release est finalisée par le serveur ; `version` = branche poussée. */
  needsFinalize: boolean;
  containerId: string | null;
  version: string | null;
  conflicts?: string[];
}

/** Réponse d'« Envoyer » : publiée tout de suite (ayant droit) ou demande envoyée. */
export interface SendResultDTO {
  request: DeployRequestDTO;
  /** L'appelant avait le droit de publier : la publication a été tentée. */
  direct: boolean;
  published: boolean;
  result: PublishOutcomeDTO | null;
  message: string;
}

/** Une version d'un fichier de réglages et les images qui la portent. */
export interface DivergenceVariantDTO {
  containerIds: string[];
  containerNames: string[];
  reference: boolean;
  /** Chemins de clés qui s'écartent de la référence (jamais de valeur). */
  differs: string[];
}

export interface DivergenceFileDTO {
  /** Chemin sous le dossier des mods. */
  path: string;
  variants: DivergenceVariantDTO[];
  fingerprint: string;
  /** Écart marqué « voulu » pour cet état exact des fichiers. */
  acknowledged: boolean;
  acknowledgedBy: string | null;
  acknowledgedAt: number | null;
}

/** Écarts de réglages entre les images du projet actif. */
export interface DivergenceReportDTO {
  project: string;
  images: { id: string; name: string }[];
  /** Images non lues, avec la raison. */
  notCompared: string[];
  /** Fichiers portés par au moins deux images. */
  comparedFiles: number;
  /** Parmi eux, identiques partout (sections propres au serveur exclues). */
  identicalFiles: number;
  files: DivergenceFileDTO[];
  generatedAt: number;
}

export interface EssentialsGroup {
  name: string;
  homeLimit: number | null;
  chatFormat: string | null;
  premiumChatFormat: string | null;
}

export interface EssentialsData {
  found: boolean;
  file: string | null;
  groups: EssentialsGroup[];
}

export interface PremiumGroup {
  name: string;
  values: Record<string, unknown>;
}

export interface PremiumData {
  found: boolean;
  file: string | null;
  groups: PremiumGroup[];
  nametags: string[];
  ranktags: string[];
  /** nom → index déclaré côté serveur : à comparer à l'index du pack (un écart = mauvaise texture en jeu). */
  nametagIndexes?: Record<string, number>;
  ranktagIndexes?: Record<string, number>;
}

export interface RealignResult {
  success: boolean;
  message: string;
  report: string[];
}

export interface CosmeticCreateResult {
  success: boolean;
  message: string;
  index: number;
  cosmeticName: string | null;
  groupId: string | null;
  editedFiles: string[];
  containers: string[];
}

export interface PackTreeResponse {
  entries: PackFileNode[];
}

// Pack content (blocks / items / entities)
export interface PackBlockDTO {
  identifier: string;
  pack: string;
  source: string;
  sourceType: string; // block | item_block | resource_only
  texture: string; // clé de texture du matériau "*"
  geometry: string;
  geometryFile: string | null;
  renderMethod: string;
  sound: string;
  textures: Record<string, string>; // face -> chemin png relatif au groupe
  thumbnail: string | null;
  orientable: boolean;
  drop: string; // item droppé (vide = lui-même)
  collision: PackBlockBox;
  selection: PackBlockBox;
  /** Bloc converti en objet (propsi:oversized) ; géométrie/texture ci-dessus = celles de l'affichage. */
  oversized?: PackOversizedInfo | null;
}

/** Offset d'empreinte d'un objet (repère monde, orientation nord, base en 0,0,0). */
export interface PackCellOffset { x: number; y: number; z: number; }

export interface PackOversizedInfo {
  displayEntity: string;
  footprint: string; // 'cells' | 'display_only'
  cells: PackCellOffset[];
  passable: boolean;
  scale: number;
  displayFile: string | null;
}

export type PackOversizedMode = 'auto' | 'force' | 'off';

export interface PackOversizedInput {
  mode: PackOversizedMode;
  footprint?: string | null;
  cells?: PackCellOffset[] | null;
  passable?: boolean | null;
  threshold?: number | null;
  scale?: number | null;
}

export interface PackGeometryCell extends PackCellOffset {
  fill: number; // 0..1
  base: boolean;
  reachable: boolean;
}

/** Analyse « trop gros pour un bloc » d'une géométrie (GeometryBounds côté API). */
export interface PackGeometryAnalysis {
  empty: boolean;
  verdict: 'fits' | 'overflow' | 'too_big' | 'limit';
  strictVerdict: 'fits' | 'overflow' | 'too_big';
  min: number[];
  max: number[];
  worldMin: number[];
  worldMax: number[];
  size: number[];
  rotated: boolean;
  reasons: string[];
  warnings: string[];
  summary: string;
  threshold: number;
  cells: PackGeometryCell[];
  footprint: PackCellOffset[];
  proposedMode: 'object' | 'block';
  proposedFootprint: 'cells' | 'display_only';
  footprintReason: string;
  cubes: number;
}

export interface PackOversizedBatchInput {
  dryRun?: boolean;
  identifiers?: string[] | null;
  threshold?: number | null;
}

export interface PackOversizedBatchEntry {
  identifier: string;
  geometry: string;
  verdict: string;
  strictVerdict: string;
  summary: string;
  object: boolean;
  action: 'convert' | 'converted' | 'already' | 'skip' | 'error';
  footprint: string;
  cells: PackCellOffset[];
  message: string;
}

export interface PackOversizedBatchReport {
  success: boolean;
  dryRun: boolean;
  message: string;
  counts: Record<string, number>;
  entries: PackOversizedBatchEntry[];
}

export interface PackBlockBox {
  mode: string; // 'full' | 'none' | 'custom'
  origin: number[] | null;
  size: number[] | null;
}

export interface PackItemDTO {
  identifier: string;
  pack: string;
  source: string;
  iconKey: string;
  iconPath: string | null;
}

export interface PackEntityDTO {
  identifier: string;
  pack: string;
  source: string | null;
  rpSource: string | null;
  geometry: string;
  geometryFile: string | null;
  textures: Record<string, string>;
  renderControllers: string[];
  componentKeys: string[];
  collisionWidth: number | null;
  collisionHeight: number | null;
}

export interface PackEntitySaveInput {
  identifier: string;
  source?: string | null;
  rpSource?: string | null;
  geometry?: string | null;
  texture?: string | null;
  translations?: Record<string, string>;
  spawnable?: boolean | null;
  summonable?: boolean | null;
  health?: number | null;
  collisionWidth?: number | null;
  collisionHeight?: number | null;
  hasCollision?: boolean | null;
  hasGravity?: boolean | null;
  fireImmune?: boolean | null;
  movement?: number | null;
  scale?: number | null;
  tags?: string[] | null;
  timerLooping?: boolean | null;
  timerTime?: number | null;
}

export interface PackBlocksResponse { blocks: PackBlockDTO[]; }
export interface PackItemsResponse { items: PackItemDTO[]; }
export interface PackEntitiesResponse { entities: PackEntityDTO[]; }

export interface PackSaveResult {
  success: boolean;
  message: string;
}

export interface PackGeometryRef { id: string; file: string; }
export interface PackTextureRef { key: string; path: string; }
export interface PackGeometriesResponse { geometries: PackGeometryRef[]; }
export interface PackTexturesResponse { textures: PackTextureRef[]; }
export interface PackLangResponse { translations: Record<string, string>; }
export interface PackImportResult { success: boolean; message: string; value: string | null; }

export interface PackBlockSaveInput {
  identifier: string;
  behaviorFile?: string;
  texture?: string;
  renderMethod?: string;
  geometry?: string;
  sound?: string;
  translations?: Record<string, string>;
  light?: number | null;
  hardness?: number | null;
  explosionResistance?: number | null;
  drop?: string | null;
  orientable?: boolean | null;
  collisionBox?: PackBlockBox | null;
  selectionBox?: PackBlockBox | null;
  toolType?: number | null;
  toolTier?: number | null;
  tags?: string[] | null;
  xpOre?: boolean | null;
  xpDeepslate?: boolean | null;
  /** Gros bloc de déco ; absent = inchangé (un objet reste un objet). */
  oversized?: PackOversizedInput | null;
}

export interface PackItemSaveInput {
  identifier: string;
  source?: string;
  maxStack?: number | null;
  icon?: string;
  translations?: Record<string, string>;
  creativeCategory?: string | null;
  tags?: string[] | null;
  durability?: number | null;
  damage?: number | null;
  enchantSlot?: string | null;
  enchantValue?: number | null;
  handEquipped?: boolean | null;
  toolKind?: string | null;
  toolTier?: number | null;
  toolMultiplier?: number | null;
  armorSlot?: string | null;
  armorProtection?: number | null;
  armorToughness?: number | null;
  foodNutrition?: number | null;
  foodSaturation?: number | null;
  foodAlwaysEat?: boolean | null;
}

export interface PackTextureImportInput {
  key?: string;
  fileName?: string;
  fileData?: string;
  atlas?: string; // 'terrain' | 'item'
}

export interface PackSoundDef {
  name: string;
  file: string | null; // .ogg relatif au groupe (pour lecture), ou null
}
export interface PackSoundsResponse { sounds: PackSoundDef[]; }

export interface PackRecipe {
  exists: boolean;
  type: string; // shaped | shapeless | furnace | none
  grid: string[]; // 9 identifiants d'items ("" = vide)
  count: number;
  input: string; // four : item d'entrée
  experience: number; // four : XP
  /** Chemin réel du fichier, à renvoyer tel quel à l'enregistrement pour ne pas créer de doublon. */
  source: string;
}

export interface PackRecipeDTO {
  identifier: string; // résultat
  type: string; // shaped | shapeless | furnace
  source: string;
  pack: string;
  resultIcon: string | null;
}

export interface PackRecipesResponse {
  recipes: PackRecipeDTO[];
}

export interface PackAttachable {
  exists: boolean;
  texture: string | null;
  geometry: string | null;
  geometryFile: string | null;
}

export interface PackAttachableSaveInput {
  identifier: string;
  geometry: string;
  texture: string;
  enchantedTexture?: string;
}

export interface PackDropEntry {
  item: string;
  count?: number | null;
  min?: number | null;
  max?: number | null;
  random: boolean;
}
export interface PackDrop {
  exists: boolean;
  entries: PackDropEntry[];
  requireTool: boolean;
  fortune: boolean;
}
export interface PackDropSaveInput {
  identifier: string;
  behaviorFile?: string;
  entries: PackDropEntry[];
  requireTool: boolean;
  fortune: boolean;
}

export interface PackRecipeSaveInput {
  identifier: string;
  type: string; // 'shaped' (classique) | 'shapeless' (sans forme) | 'furnace' (four)
  grid?: string[];
  count?: number;
  input?: string;
  experience?: number;
  /**
   * Fichier à réécrire, tel que renvoyé par la lecture. Omis, le backend recalcule un chemin
   * canonique et crée un second fichier : l'ancienne recette reste alors active en jeu.
   */
  source?: string;
}

/** Clé de box trouvée dans un groupe de packs. */
export interface BoxKeyDTO {
  identifier: string;
  /** Id de la box ouverte par la clé. */
  box: string;
  /** Composant qui la porte : premium:lootbox_key, ou son alias premium:box_key. */
  component: string;
  /** Fichier behavior de l'item, relatif au groupe. */
  source: string;
  iconKey: string;
  /** PNG de l'icône, relatif au groupe. */
  iconPath: string | null;
  /** Attachable 3D, relatif au groupe (null si aucun). */
  attachable: string | null;
  /** Nom affiché par langue (en_US, fr_FR). */
  names: Record<string, string>;
}

export interface BoxKeysResponse {
  exists: boolean;
  resourceReadonly: boolean;
  keys: BoxKeyDTO[];
}

export interface BoxKeyInput {
  identifier: string;
  box: string;
  names: Record<string, string>;
  /** Icône PNG (data URL). */
  iconData: string;
  /** Attachable 3D, copié de celui d'une clé existante. */
  attachable: boolean;
  /** Clé dont l'attachable sert de modèle (défaut serveur : la première qui en a un). */
  templateKey?: string;
  /** Texture PNG du modèle 3D (data URL) ; omise, celle du modèle est reprise. */
  modelTextureData?: string;
}

export interface BoxKeyResult {
  success: boolean;
  message: string;
  identifier: string | null;
  /** Fichiers écrits, relatifs au groupe. */
  files: string[];
}

/** Désigne un fichier de config dans chaque container du projet et les sections gérées par la page. */
export interface ConfigSectionsQuery {
  /** Suffixes de chemin acceptés, par ordre de préférence (ex. ['jobs.config', 'jobs.config.json']). */
  fileNames: string[];
  /** Sections de premier niveau gérées par la page. Une section propre au serveur est refusée. */
  keys: string[];
  /**
   * Clé exigée à la racine du JSON, pour les fichiers sans nom fixe (ex. Loots). Loots et Rewards
   * désignent un fichier de récompenses : le serveur borne alors `keys` à sa liste blanche.
   */
  requiredKey?: string;
  requiredKind?: 'array' | 'object';
  /** Clé métier d'identité (ex. Id d'une lootbox), pour les fichiers dont le nom varie. */
  matchKey?: string;
  matchValue?: string;
  /** Fichier de référence imposé (celui que la page a listé), container et chemin ensemble. */
  sourceContainerId?: string;
  sourcePath?: string;
  /**
   * Mod propriétaire du fichier : le serveur ne retient que les containers dont l'image porte ce mod
   * (release-version.json), cherche le fichier là où le mod le lit et borne `keys` à sa liste
   * blanche. JobsMod et RecubeJobsMod lisent tous deux data/mods/jobs.config.json ; RecubeICBM lit
   * data/mods/recubeicbm.config.json (page Armes : sections weapons et weapon_rules) ; WorldOriginMod lit
   * data/mods/worldorigin.config.json (page Génération de minerais : section Levels, dont le serveur ne
   * prend que Ores et AutoRegen de chaque monde).
   */
  mod?: ConfigMod;
}

/** Mods dont le fichier de config est désigné par le mod et non par son seul nom. */
export type ConfigMod = 'JobsMod' | 'RecubeJobsMod' | 'RecubeICBM' | 'WorldOriginMod';

export interface ConfigSectionsApplyInput extends ConfigSectionsQuery {
  /** Valeurs des sections à écrire partout. */
  sections: Record<string, unknown>;
  /** Sections explicitement vidées par la page, retirées partout. */
  removeKeys?: string[];
  /** Création : nom du fichier à créer dans chaque container qui ne porte pas encore cette identité. */
  createFileName?: string;
  /** Création : dossier de repli, utilisé s'il existe dans le container. */
  createDir?: string;
}

/** État d'un container à l'inspection (FOUND : présent mais non comparé, la référence étant illisible). */
export type ConfigInspectStatus =
  | 'REFERENCE'
  | 'SAME'
  | 'DIFFERENT'
  | 'FOUND'
  | 'SKIPPED_NOT_SYNCED'
  | 'SKIPPED_FILE_MISSING'
  | 'SKIPPED_AMBIGUOUS'
  | 'SKIPPED_INVALID_JSON'
  | ConfigModSkipStatus
  | 'FAILED';

/**
 * Container écarté par le mod de la requête : mod absent de l'image, présent avec un mod qui lit le
 * même fichier, ou non identifiable (ni release-version.json, ni section propre au mod).
 */
export type ConfigModSkipStatus = 'SKIPPED_MOD_ABSENT' | 'SKIPPED_MOD_SHARED' | 'SKIPPED_MOD_UNKNOWN';

export interface ConfigInspectEntry {
  containerId: string;
  containerName: string;
  status: ConfigInspectStatus;
  path: string | null;
  message: string;
  /** Sections qui diffèrent de la référence (statut DIFFERENT). */
  differs: string[];
}

export interface ConfigInspection {
  success: boolean;
  message: string;
  referenceContainerId: string | null;
  referenceContainerName: string | null;
  referencePath: string | null;
  /** Sections demandées présentes dans la référence, et elles seules. */
  sections: Record<string, unknown> | null;
  /** Nom des autres sections du fichier de référence (jamais leur contenu). */
  otherKeys: string[];
  entries: ConfigInspectEntry[];
  /**
   * Champs que le mod de la requête montre sans les rendre éditables (ex. ServerSettings.Server de
   * RecubeJobsMod), par section ; null sans mod.
   */
  readOnly?: Record<string, Record<string, unknown>> | null;
  /**
   * Ce que le serveur sait du mod en dehors du fichier : pour RecubeICBM, la table des armes (valeurs
   * par défaut, bornes, catégories, missiles, version du mod dans chaque image), typée par la page
   * Armes (WeaponsCatalog). Null pour les autres mods et sans container de référence.
   */
  catalog?: unknown;
}

export type ContainerApplyStatus =
  | 'WRITTEN'
  | 'CREATED'
  | 'UNCHANGED'
  | 'SKIPPED_NOT_SYNCED'
  | 'SKIPPED_FILE_MISSING'
  | 'SKIPPED_AMBIGUOUS'
  | 'SKIPPED_INVALID_JSON'
  | 'SKIPPED_EXISTS'
  | ConfigModSkipStatus
  | 'FAILED';

export interface ContainerApplyEntry {
  containerId: string;
  containerName: string;
  status: ContainerApplyStatus;
  path: string | null;
  message: string;
}

export interface ContainerApplyResult {
  success: boolean;
  message: string;
  sourcePath: string | null;
  keys: string[];
  entries: ContainerApplyEntry[];
}

export const apiService = new ApiService();
